import React, { useContext, useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AuthContext, RoleContext, ThemeContext, DataStateContext, FilterContext, AdvanceContext, DataActionsContext, SettingsContext, DivisionLensContext } from "@/App";
import { PIPELINE_DIVISIONS, divisionByKey, divisionColor, itemDivision } from "@/lib/divisions";
import DivisionGlyph, { AllDivisionsGlyph } from "@/components/icons/DivisionGlyph";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import GlobalFilterPanel, { GlobalFilterContent } from "@/components/GlobalFilterPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AdminSettingsPanel from "@/components/AdminSettingsPanel";
import { toast } from "@/components/ui/sonner";
import axios from "axios";
import {
  LayoutDashboard, Package, ShoppingCart, Factory, ClipboardCheck, Layers,
  Truck, CheckCircle2, Sun, Moon, History, LogOut, User, SlidersHorizontal,
  Settings, WifiOff, Wifi, MessageSquare, Loader2, Users, Circle,
  Sparkles, Flame, Rows, Mail, Link2, Recycle,
  Eye, Activity, Database, Filter, FileSpreadsheet,
  Bell, StickyNote, AtSign, ShieldAlert, Calendar, RefreshCw,
  X as XIcon, Megaphone, CheckCheck, Target,
  MoreVertical, Search, ChevronDown, Warehouse, Gauge, Glasses, Monitor, Globe,
  LayoutGrid, ArrowLeftRight, FileDiff, Boxes, PackageCheck, IndianRupee,
  Lock, Wrench, Hourglass, CalendarClock, Briefcase, EyeOff, MailCheck, Keyboard,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommandPalette from "@/components/CommandPalette";
import KeyboardShortcutsGuide from "@/components/KeyboardShortcutsGuide";
import StatusGlyph from "./icons/StatusGlyph";
import MentionInbox from "./MentionInbox";
import MentionNotifBanner from "./MentionNotifBanner";
import StageInboxBell from "./StageInboxBell";
import AnalystButton from "@/components/analyst/AnalystButton";
import AnalystGlyph from "@/components/analyst/AnalystGlyph";
import PipelineRail from "@/components/PipelineRail";
import WorkspaceWaffle from "@/components/WorkspaceWaffle";
import { PipelineTrendContext, useLoadPipelineTrend } from "@/contexts/PipelineTrendContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import IconUpdateNotice from "./IconUpdateNotice";
import InstallAppLink from "./InstallAppLink";
import { useLoginLook, accentOf } from "@/lib/greetings";
import { useDueFilter } from "@/contexts/DueFilterContext";
import { itemMatchesDueFilter } from "@/components/DueFilterPopover";
import { parseDate } from "@/components/StagePage/utils";
import { useTableDensity } from "@/hooks/useTableDensity";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// After a division switch the rail locks briefly (anti-thrash + lets the wash
// morph settle); a clockwise ring draws once over this window. Tune here.
const RAIL_COOLDOWN_MS = 1500;

// Mobile "Pipeline" glyph (29-Jul) — a miniature of the desktop arrow-ribbon
// stage rail, so the mobile trigger speaks the same language as desktop. Three
// nested chevron tabs, back-to-front opacity ramp; inherits currentColor.
// Three tabs, each carrying a gradient across a PAIR of consecutive stage
// colours, so all six pipeline hues appear in order (PO Received -> Completed)
// without turning to mush at 24px. Gradient ids are module-constant: only one
// instance is ever mounted (mobile trigger, neutral state).
const PipelineRibbonIcon = ({ className }) => (
  <svg viewBox="0 0 30 20" className={className} aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="rlRib1" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#f97316" />
      </linearGradient>
      <linearGradient id="rlRib2" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#a855f7" /><stop offset="100%" stopColor="#14b8a6" />
      </linearGradient>
      <linearGradient id="rlRib3" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#f59e0b" /><stop offset="100%" stopColor="#94a3b8" />
      </linearGradient>
    </defs>
    <path d="M1 3h7l4 7-4 7H1l4-7z" fill="url(#rlRib1)" />
    <path d="M11 3h7l4 7-4 7h-7l4-7z" fill="url(#rlRib2)" />
    <path d="M21 3h6l2.5 7-2.5 7h-6l4-7z" fill="url(#rlRib3)" />
  </svg>
);

const ALL_STAGES_NAV = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard, stage: null },
  { label: "PO Received", path: "/po-received", icon: Package, stage: "PO Received", color: "#3b82f6", role: "Received" },
  { label: "Procurement", path: "/procurement", icon: ShoppingCart, stage: "Procurement", color: "#f97316", role: "Procurement" },
  { label: "Manufacturing", path: "/manufacturing", icon: Factory, stage: "Manufacturing", color: "#a855f7", role: "Manufacturing" },
  { label: "QC Check", path: "/qc-check", icon: ClipboardCheck, stage: "Quality Control", color: "#14b8a6", role: "QC" },
  { label: "Dispatch", path: "/dispatch", icon: Truck, stage: "Dispatch", color: "#f59e0b", role: "Dispatch" },
  { label: "Completed", path: "/completed", icon: CheckCircle2, stage: "Completed", color: "#94a3b8", role: null },
];

const ROLE_BADGES = {
  Admin: { label: "Admin", color: "bg-orange-500/15 text-orange-500 border-orange-500/30" },
  Received: { label: "PO Received", color: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
  Procurement: { label: "Procurement", color: "bg-orange-500/15 text-orange-500 border-orange-500/30" },
  Manufacturing: { label: "Manufacturing", color: "bg-purple-500/15 text-purple-500 border-purple-500/30" },
  QC: { label: "QC", color: "bg-teal-500/15 text-teal-500 border-teal-500/30" },
  Dispatch: { label: "Dispatch", color: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  Stores: { label: "Stores", color: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  // Module-only roles — they reach the ERP header only as dual-access users,
  // but the badge has to know their name either way.
  "Stock Center": { label: "Stock Center", color: "bg-sky-500/15 text-sky-500 border-sky-500/30" },
  Ecotile: { label: "Ecotile", color: "bg-green-600/15 text-green-600 border-green-600/30" },
  Maintenance: { label: "Maintenance", color: "bg-[#2F6DAE]/15 text-[#2F6DAE] dark:text-[#7FB2E5] border-[#2F6DAE]/30" },
  Viewer: { label: "Viewer", color: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
};

const LOGO_URL = "/rockpecker-logo.png";
const LOGO_URL_DRILLING = "/logo-rockpecker-drilling.webp";  // Rockpecker Drilling wordmark (Stock Center's)

const SyncStatusIndicator = React.memo(function SyncStatusIndicator({
  lastRefreshTime, isRefreshing, presenceUsers, onPresenceClick, onOfflineClick, onBackOnline,
  onSync, syncBusy = false, justRefreshed = false,
}) {
  const [syncAge, setSyncAge] = useState(0);
  const [isOnline, setIsOnline] = useState(true);
  const wasOnlineRef = useRef(true);

  useEffect(() => {
    const tick = () => setSyncAge(Math.floor((Date.now() - (lastRefreshTime || Date.now())) / 1000));
    tick();
    const id = setInterval(tick, 10_000);
    return () => clearInterval(id);
  }, [lastRefreshTime]);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/health`, { method: "GET", cache: "no-store", signal: AbortSignal.timeout(5000) });
        const ok = res.ok;
        setIsOnline(ok);
        if (ok && !wasOnlineRef.current) {
          wasOnlineRef.current = true;
          onBackOnline();
        } else if (!ok && wasOnlineRef.current) {
          wasOnlineRef.current = false;
          onOfflineClick();
        }
      } catch {
        setIsOnline(false);
        if (wasOnlineRef.current) {
          wasOnlineRef.current = false;
          onOfflineClick();
        }
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 10000);
    return () => clearInterval(interval);
  }, [onOfflineClick, onBackOnline]);

  const onlineCount = presenceUsers.filter(u => u.is_online).length;
  const [presenceOpen, setPresenceOpen] = useState(false);

  // Capsule tint — colours the WHOLE split pill (presence segment + sync
  // segment) so both halves always agree on the connection state.
  const tintCls = isOnline && presenceOpen
    ? 'bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]'
    : !isOnline
      ? 'text-red-400 rl-frost-tint rl-tint-red'
      : isRefreshing
        ? 'text-blue-400 rl-frost-tint rl-tint-blue'
        : syncAge < 60
          ? 'text-green-500 rl-frost-tint rl-tint-green'
          : syncAge < 300
            ? 'text-[hsl(var(--muted-foreground))] rl-frost-btn'
            : 'text-amber-500 rl-frost-tint rl-tint-amber';

  const pillButton = (
    <button
      onClick={() => {
        // Offline: keep the existing recovery action (refresh / details).
        // Online: open the presence popover instead of the lightbox.
        if (!isOnline) { onOfflineClick(); return; }
        setPresenceOpen(o => !o);
      }}
      className={`flex items-center gap-1.5 h-8 pl-3 ${onSync ? "pr-2" : "pr-3"} text-[11px] font-semibold cursor-pointer`}
      data-testid="online-status-btn"
    >
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${
          !isOnline
            ? 'bg-red-500 offline-dot'
            : isRefreshing
              ? 'bg-blue-400 animate-pulse'
              : syncAge < 60
                ? 'bg-green-500 animate-pulse'
                : syncAge < 300
                  ? 'bg-[hsl(var(--muted-foreground))]/50'
                  : 'bg-amber-500'
        }`}
      />
      <span className="hidden sm:inline">
        {!isOnline
          ? "Offline"
          : isRefreshing
            ? "Syncing…"
            : syncAge < 60
              ? (onlineCount > 0 ? `${onlineCount} online` : "Live")
              : `${Math.floor(syncAge / 60)}m ago`}
      </span>
    </button>
  );

  const tooltipText = !isOnline
    ? "Server unreachable — click for details"
    : isRefreshing
      ? "Syncing latest data…"
      : syncAge < 60
        ? "Data is live — click to see who's online"
        : `Last synced ${Math.floor(syncAge / 60)}m ago — click to see who's online`;

  return (
    <Popover open={isOnline && presenceOpen} onOpenChange={(o) => { if (isOnline) setPresenceOpen(o); }}>
      <span className={`flex items-center rounded-full transition-[colors,box-shadow] ${tintCls}`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{pillButton}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent><p>{tooltipText}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Sync segment — the action lives beside the status it affects. */}
        {onSync && (
          <>
            <span className="w-px self-stretch my-2 bg-current opacity-25" aria-hidden="true" />
            <button
              type="button"
              onClick={onSync}
              disabled={syncBusy}
              className={`flex items-center justify-center h-8 pl-2 pr-2.5 transition-opacity disabled:cursor-not-allowed ${
                justRefreshed ? "text-emerald-500 opacity-100" : "opacity-60 hover:opacity-100"
              }`}
              title={syncBusy ? "Refreshing…" : justRefreshed ? "Up to date" : "Sync latest data"}
              aria-label="Refresh data"
              data-testid="refresh-data-btn"
            >
              <RefreshCw className={`w-3 h-3 ${syncBusy ? "animate-spin" : ""}`} strokeWidth={2.2} />
            </button>
          </>
        )}
      </span>
      <PopoverContent align="end" className="w-[min(300px,92vw)] max-h-[60vh] overflow-auto p-3 rl-frost border-[hsl(var(--border))]">
        <PresenceList users={presenceUsers} loading={false} compact />
      </PopoverContent>
    </Popover>
  );
});

/* ── Theme slider (Light / Auto / Dark) ─────────────────────────────────────
 * Carved capsule + lifted thumb — same vocabulary as the density toggle.
 * "Auto" (= pref "system") follows the device's day/night setting live.
 * Rendered inside both the desktop user menu and the mobile overflow menu;
 * lives OUTSIDE DropdownMenuItem so picking a theme doesn't close the menu
 * (the user sees the switch happen immediately). */
const THEME_OPTIONS = [
  { key: "light",  icon: Sun,     label: "Light" },
  { key: "system", icon: Monitor, label: "Auto"  },
  { key: "dark",   icon: Moon,    label: "Dark"  },
];

function ThemeSlider({ themePref, setThemePref }) {
  return (
    <div className="px-3 pt-2 pb-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">
        Appearance
      </p>
      <div
        className="rl-carved flex gap-0.5 p-0.5 rounded-full bg-[hsl(var(--muted))]/50"
        role="radiogroup"
        aria-label="Theme"
        data-testid="theme-slider"
      >
        {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={themePref === key}
            onClick={() => setThemePref(key)}
            data-testid={`theme-${key}-btn`}
            className={`flex-1 flex items-center justify-center gap-1 h-6 rounded-full text-[11px] font-medium transition-colors ${
              themePref === key
                ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>
      {themePref === "system" && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5 text-center whitespace-nowrap">
          Follows your device setting
        </p>
      )}
    </div>
  );
}

/* Performance-mode row — a real switch instead of a command row with an
 * ON/OFF chip. Lives OUTSIDE DropdownMenuItem (same as ThemeSlider) so
 * flipping it doesn't close the menu: the user can watch the depth effects
 * drop away and flip straight back if they prefer them. */
function FlatModeRow({ flatMode, toggleFlat }) {
  return (
    <div className="px-3 pt-1.5 pb-2" data-testid="flat-mode-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-[hsl(var(--foreground))]">
          <Gauge className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
          Reduce visual effects
        </span>
        <Switch
          checked={flatMode}
          onCheckedChange={toggleFlat}
          aria-label="Reduce visual effects"
          data-testid="flat-mode-switch"
        />
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 pl-6">
        Snappier on older devices
      </p>
    </div>
  );
}

/* Easy-read text row — same shape as FlatModeRow. Swaps the type tokens for a
 * rounder, more open set; stays outside DropdownMenuItem so the menu stays
 * open and the user can flip it back while watching the text change. */
function EasyReadRow({ easyRead, toggleEasyRead }) {
  return (
    <div className="px-3 pt-0.5 pb-2" data-testid="easyread-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-[hsl(var(--foreground))]">
          <Glasses className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
          Easy-read text
        </span>
        <Switch
          checked={easyRead}
          onCheckedChange={toggleEasyRead}
          aria-label="Easy-read text"
          data-testid="easyread-switch"
        />
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 pl-6">
        Rounder, more open letters for long sessions
      </p>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useContext(AuthContext);
  const { refreshData } = useContext(DataActionsContext) || {};
  const { themePref, setThemePref, cycleTheme, flatMode, toggleFlat, easyRead, toggleEasyRead } = useContext(ThemeContext);
  const { items, isRefreshing, lastRefreshTime } = useContext(DataStateContext);
  const { settings } = useContext(SettingsContext) || {};
  const { dueFilter } = useDueFilter();
  const { globalFilter, setGlobalFilter } = useContext(FilterContext);
  const { lens: divisionLens, setLens: setDivisionLens, lockedDivision } = useContext(DivisionLensContext) || { lens: "all" };
  const { lastAdvanceInfo } = useContext(AdvanceContext);
  const location = useLocation();
  const navigate = useNavigate();
  // ── Division rail glass thumb (segmented control, 16-Jul) ──
  // Every pill is permanently full-size (icon + label). The lens commits
  // instantly on click; the one glass thumb simply slides between the fixed
  // pills — CSS handles the glide. No pill ever folds or unfolds, so there is
  // nothing to sequence (that was the "expands before it's set" irritation).
  // Instant snap under Performance Mode / prefers-reduced-motion.
  const railThumbRef = useRef(null);
  const railPillRefs = useRef(new Map());
  const railReduced = flatMode || (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

  // Pills are ALWAYS full-size (icon + label). Nothing ever changes shape, so
  // there is no fold/unfold to sequence — the glass thumb simply reads the
  // target pill's FIXED geometry and slides there; the CSS transition on
  // .rl-rail-thumb does the glide. `instant:true` snaps (initial mount, resize,
  // reduced motion). Deeper shadow while moving comes from the .moving class.
  const placeRailThumb = useCallback((key, { instant = false, _tries = 0 } = {}) => {
    const pill = railPillRefs.current.get(key);
    const thumb = railThumbRef.current;
    if (!thumb) return;
    // On first paint the pill ref may be absent or report width 0 (layout/fonts
    // not settled). Retry on the next frame so the initial snap never lands on
    // a zero-width thumb — up to a few tries, then give up.
    if ((!pill || pill.offsetWidth === 0) && _tries < 10) {
      requestAnimationFrame(() => placeRailThumb(key, { instant, _tries: _tries + 1 }));
      return;
    }
    if (!pill) return;
    if (instant) thumb.style.transition = "none";
    thumb.style.width = `${pill.offsetWidth}px`;
    thumb.style.transform = `translateX(${pill.offsetLeft}px)`;
    if (instant) { void thumb.offsetHeight; thumb.style.transition = ""; }
  }, []);

  const railClickRef = useRef(false);           // true while a lens change came from a click (should glide, not snap)
  const [railCooldown, setRailCooldown] = useState(false);   // rail locked + ring drawing
  const railCooldownTimer = useRef(null);       // truthy while cooling (synchronous click guard)
  useEffect(() => () => { if (railCooldownTimer.current) clearTimeout(railCooldownTimer.current); }, []);
  const switchLens = useCallback((key) => {
    if (key === divisionLens) return;
    if (railCooldownTimer.current) return;      // still cooling down — ignore the click
    railClickRef.current = true;                // tell the layout effect NOT to snap this change
    setDivisionLens(key);                       // commit immediately (content never waits)
    // The thumb glides from the layout effect below, AFTER the commit: only
    // the selected pill carries its label (2-Nov-2026), so the target's slot
    // is only known once the new labels are in the DOM.
    // Lock the switcher for the cooldown window; the ring (below) counts it out.
    // Skip in reduced/flat mode — no motion to protect there.
    if (!railReduced) {
      setRailCooldown(true);
      railCooldownTimer.current = setTimeout(() => {
        railCooldownTimer.current = null;
        setRailCooldown(false);
      }, RAIL_COOLDOWN_MS);
    }
  }, [divisionLens, setDivisionLens, railReduced, placeRailThumb]);

  // Snap instantly on MOUNT and non-click lens changes (login restore, resize).
  // A click-driven change must NOT snap here — that would kill the glide the
  // click just started — so we skip the instant placement when railClickRef is
  // set (and clear the flag). useLayoutEffect runs before paint (no rAF freeze
  // in background tabs); a rAF top-up handles late font/layout settle on mount.
  // The switch lives in the header brand block from md up and in its own row
  // on phones (only one is mounted), so crossing 768px re-snaps the new thumb.
  const isDesktop = useMediaQuery("(min-width: 768px)");
  useLayoutEffect(() => {
    if (railClickRef.current) {
      // Click: glide (CSS transition) to the target's new, labelled slot.
      railClickRef.current = false;
      placeRailThumb(divisionLens, { instant: railReduced });
      return;
    }
    placeRailThumb(divisionLens, { instant: true });
    const raf = requestAnimationFrame(() => placeRailThumb(divisionLens, { instant: true }));
    return () => cancelAnimationFrame(raf);
  }, [divisionLens, placeRailThumb, isDesktop]);
  // Resize repositions instantly (pill widths can change with font/zoom).
  useEffect(() => {
    const onResize = () => placeRailThumb(divisionLens, { instant: true });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [divisionLens, placeRailThumb]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);  // desktop filter popover open-state (for pressed-in trigger)
  const [adminSettingsOpen, setAdminSettingsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [justRefreshed, setJustRefreshed] = useState(false);
  // Closing the announcement is remembered in this browser (keyed by the
  // message text), so it stays shut across refreshes until the admin posts a
  // different message.
  const [dismissedNotice, setDismissedNotice] = useState(() => {
    try { return localStorage.getItem("rl-notice-closed") || ""; } catch { return ""; }
  });
  const [noticeClosing, setNoticeClosing] = useState(false);
  // Holiday greeting (Admin → Greetings) shares the drawer as its own line.
  // Closing it is remembered per greeting + edit, like the announcement.
  const { look } = useLoginLook();
  const [dismissedGreeting, setDismissedGreeting] = useState(() => {
    try { return localStorage.getItem("rl-greeting-closed") || ""; } catch { return ""; }
  });
  // True once the page has scrolled at all — the header then flattens its
  // rounded "sheet" top (see .rl-sheet-top) so it sits square while stuck.
  const [pageScrolled, setPageScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setPageScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // How search was last opened: a mouse click shows the shortcuts tip at the
  // bottom every time (keyboard users already know). Tushar wants it to stay:
  // its × only hides it until search is next opened.
  const [paletteByClick, setPaletteByClick] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // The guide catches keys itself; this ref keeps the app's handler below
  // quiet while it is open, as a backstop.
  const shortcutsOpenRef = useRef(false);
  shortcutsOpenRef.current = shortcutsOpen;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [stageMenuOpen, setStageMenuOpen] = useState(false); // mobile stage-selector dropdown open state
  const [gHint, setGHint] = useState(null); // null | { key, label, color, leaving }
  const gHintT = useRef({});
  const gHintSeq = useRef(0); // increments on every press so the keycap animation replays even for repeated keys
  const { density, setDensity } = useTableDensity();
  const densityRef = useRef(density);
  useEffect(() => { densityRef.current = density; }, [density]);
  const [hintPhase, setHintPhase] = useState(0);
  const platform = useMemo(() => {
    if (typeof navigator === 'undefined') return 'other';
    if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) return 'mobile';
    const ua = navigator.userAgent || '';
    if (/Mac|iPhone|iPad/.test(navigator.platform) || /Mac/.test(ua)) return 'mac';
    if (/Win/.test(navigator.platform) || /Windows/.test(ua)) return 'win';
    return 'other';
  }, []);
  const shortcutLabel = platform === 'mac' ? '⌘K' : platform === 'win' ? 'Ctrl+K' : null;
  useEffect(() => {
    if (!shortcutLabel) return;
    const id = setInterval(() => setHintPhase(p => (p + 1) % 12), 1500);
    return () => clearInterval(id);
  }, [shortcutLabel]); // eslint-disable-line react-hooks/exhaustive-deps
  const showIdleHint = !!shortcutLabel && !paletteOpen && hintPhase >= 10;
  const idleHintLabel = hintPhase === 11 ? '/' : shortcutLabel;

  // ── Command Palette keyboard shortcuts + G-sequence navigation ────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if (shortcutsOpenRef.current) return;
      const target = e.target;
      const isEditable = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' || target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setPaletteByClick(false); setPaletteOpen(prev => !prev); return; }
      if (e.key === 'Escape') { setPaletteOpen(false); clearTimeout(gHintT.current.life); clearTimeout(gHintT.current.gone); setGHint(null); return; }
      if (isEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') { e.preventDefault(); setPaletteOpen(false); setShortcutsOpen(true); return; }
      if (e.key === '/') { e.preventDefault(); setPaletteByClick(false); setPaletteOpen(true); return; }

      const k = e.key.toLowerCase();
      const flash = (key, label, color, theme) => {
        clearTimeout(gHintT.current.life); clearTimeout(gHintT.current.gone);
        gHintSeq.current += 1;
        setGHint({ key, label, color: color || null, theme: theme || null, leaving: false, seq: gHintSeq.current });
        gHintT.current.life = setTimeout(() => {
          setGHint(h => (h ? { ...h, leaving: true } : h));
          gHintT.current.gone = setTimeout(() => setGHint(null), 260);
        }, 1600);
      };

      const STAGE_HINT_COLOR = { 'PO Received': '#3b82f6', 'Procurement': '#f97316', 'Manufacturing': '#a855f7', 'Quality Control': '#14b8a6', 'Dispatch': '#f59e0b', 'Completed': '#94a3b8' };
      const NAV = {
        h: ['/', null, 'Dashboard'], r: ['/po-received', 'PO Received', 'PO Received'],
        p: ['/procurement', 'Procurement', 'Procurement'], m: ['/manufacturing', 'Manufacturing', 'Manufacturing'],
        q: ['/qc-check', 'Quality Control', 'QC Check'], d: ['/dispatch', 'Dispatch', 'Dispatch'],
        c: ['/completed', 'Completed', 'Completed'], l: ['/changelog', null, 'History Log'],
      };
      if (NAV[k]) {
        const [path, stage, label] = NAV[k];
        if (!stage || user?.role === 'Admin' || (canViewStage && canViewStage(stage))) {
          e.preventDefault(); navigate(path); flash(k.toUpperCase(), label, stage ? (STAGE_HINT_COLOR[stage] || null) : null);
        }
        return;
      }
      if (k === 'b') { e.preventDefault(); setGlobalFilter(f => ({ ...f, flag: f.flag === 'blocked' ? null : 'blocked' })); flash('B', 'Blocked'); return; }
      if (k === 'o') { e.preventDefault(); setGlobalFilter(f => ({ ...f, flag: f.flag === 'overdue' ? null : 'overdue' })); flash('O', 'Overdue'); return; }
      if (k === 'e') { e.preventDefault(); setGlobalFilter(f => ({ ...f, priority: f.priority === 'Expedite' ? 'all' : 'Expedite' })); flash('E', 'Expedite'); return; }
      if (k === 'x') { e.preventDefault(); setGlobalFilter({ clients: null, priority: 'all', flag: null }); flash('X', 'Clear filters'); return; }
      if (k === 'n') {
        e.preventDefault();
        const next = cycleTheme && cycleTheme();
        flash('N', 'Theme', null, next);   // rail hint glides to `next`
        return;
      }
      if (k === 'z') { e.preventDefault(); setDensity(densityRef.current === 'compact' ? 'comfortable' : 'compact'); flash('Z', 'Compact rows'); return; }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // PWA install handling now lives in <InstallAppLink/> (login + footer).

  // ── Report Problem ────────────────────────────────────────────────────────
  const [reportOpen, setReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({ category: "Bug Report", description: "" });
  const [reportSending, setReportSending] = useState(false);

  const handleReportSubmit = async () => {
    if (!reportForm.description.trim()) { toast.error("Please describe the problem"); return; }
    setReportSending(true);
    try {
      await axios.post(`${API_BASE}/api/feedback`, {
        name: user?.name || "Unknown",
        email: user?.email || "",
        category: reportForm.category,
        description: reportForm.description,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("rl_token")}` } });
      toast.success("Report submitted — thank you!");
      setReportOpen(false);
      setReportForm({ category: "Bug Report", description: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit report");
    } finally { setReportSending(false); }
  };

  // ── Rework counter for Manufacturing nav ─────────────────────────────────
  const reworkCount = useMemo(() =>
    items.filter(i => i.stage === "Manufacturing" && i.is_rework).length,
    [items]
  );
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);
  const [presenceOpen, setPresenceOpen] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState([]);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  // Versioned key — bumping it re-arms the "new" glow on the version badge
  // for every user exactly once per release.
  const RELEASE_NOTES_KEY = "rocklink.seenReleaseNotes.v2.6";
  const [hasSeenReleaseNotes, setHasSeenReleaseNotes] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(RELEASE_NOTES_KEY) === "1"
  );
  const markReleaseNotesSeen = () => {
    setHasSeenReleaseNotes(true);
    try { window.localStorage.setItem(RELEASE_NOTES_KEY, "1"); } catch (_e) { /* ignore */ }
  };
  const wasOnlineRef = useRef(true); // kept only for presence-fetch timing
  
  const fetchPresence = async () => {
    try {
      setPresenceLoading(true);
      const res = await axios.get(`${API_BASE}/api/auth/presence`);
      setPresenceUsers(res.data || []);
    } catch (e) {
      // Silent failure — presence is non-critical
    } finally {
      setPresenceLoading(false);
    }
  };

  useEffect(() => {
    // Initial presence fetch + background refresh every 60s so online pill stays fresh
    fetchPresence();
    const interval = setInterval(fetchPresence, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = user?.role === "Admin";

  const handlePresenceClick = useCallback(() => { fetchPresence(); setPresenceOpen(true); }, [fetchPresence]);
  const handleOfflineClick  = useCallback(() => setOfflineDialogOpen(true), []);
  const handleBackOnline    = useCallback(() => setOfflineDialogOpen(false), []);
  // Manual sync — lives in the online pill's sync segment.
  const handleHeaderSync = useCallback(async () => {
    if (refreshing || !refreshData) return;
    setRefreshing(true);
    try {
      await refreshData();
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 1800);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, refreshData]);

  const activeFilterCount = [
    globalFilter.clients != null && globalFilter.clients.length > 0,
    globalFilter.priority !== "all",
    globalFilter.flag != null,
  ].filter(Boolean).length;

  const { canViewStage, canDo } = useContext(RoleContext);

  const visibleNav = useMemo(() => {
    if (!user || user.role === "Admin") return ALL_STAGES_NAV;
    return ALL_STAGES_NAV.filter(item =>
      item.stage === null ||          // Dashboard always visible
      canViewStage(item.stage)        // Stage visible if user has view or edit access
    );
  }, [user, canViewStage]);

  // Division lens: bar counts + stage pages + dashboard all scope together.
  const _lensMatch = (i) => divisionLens === "all" || itemDivision(i) === divisionLens;

  // Lens-scoped items for the pipeline rail's blocked / overdue / value.
  const lensItems = useMemo(
    () => (divisionLens === "all" ? items : items.filter(i => itemDivision(i) === divisionLens)),
    [items, divisionLens]
  );
  const pipelineTrend = useLoadPipelineTrend();

  // Waffle: the permission-gated module list (RockLink ERP itself is listed
  // first by WorkspaceWaffle). Stores, Scrap Sales and Blocked Board live inside
  // this Layout, so on their pages that module is "You're here"; anywhere else
  // in this Layout it's the ERP.
  const waffleModules = [
    canDo && canDo("can_view_stores") && {
      key: "stores", to: "/stores", label: "Stores",
      desc: "Raw-material inventory", icon: Warehouse, accent: "#f97316",
      testid: "module-stores",
    },
    canDo && canDo("can_view_stock_center") && {
      key: "stock-center", to: "/stock-center", label: "Stock Center",
      desc: "Rockpecker Drilling warehouses", icon: Globe, accent: "#0F6E56",
      testid: "module-stock-center",
    },
    canDo && canDo("can_view_scrap_sales") && {
      key: "scrap-sales", to: "/scrap-sales", label: "Scrap Sales",
      desc: "Sales — factory scrap ledger", icon: IndianRupee, accent: "#7c3aed",
      testid: "module-scrap-sales",
    },
    canDo && canDo("can_view_ecotile") && {
      key: "ecotile", to: "/ecotile", label: "Ecotile",
      desc: "Ecotile flooring — orders & payments", img: "/ecotile-square-icon.png", accent: "#E6B800",
      testid: "module-ecotile",
    },
    canDo && canDo("can_view_maintenance") && {
      key: "maintenance", to: "/maintenance", label: "Maintenance",
      desc: "Machine care — breakdowns & repairs", icon: Wrench, accent: "#2F6DAE",
      testid: "module-maintenance",
    },
    canDo && canDo("can_view_blocked_board") && {
      key: "blocked-board", to: "/blocked-board", label: "Blocked Board",
      desc: "Who we're waiting on, oldest first", icon: ShieldAlert, accent: "#e11d48",
      testid: "module-blocked-board",
    },
  ].filter(Boolean);
  const waffleCurrent = waffleModules.find(m => location.pathname.startsWith(m.to))?.key || "erp";
  const waffle = (
    <WorkspaceWaffle current={waffleCurrent} modules={waffleModules} onSelect={(to) => navigate(to)} />
  );

  const getStageCount = (stage) => {
    if (!stage) return null;
    return items.filter(i => i.stage === stage && _lensMatch(i)).length;
  };

  const getFilteredStageCount = (stage) => {
    if (!stage) return null;
    const hasGlobal = (globalFilter.clients != null && globalFilter.clients.length > 0) || globalFilter.priority !== "all" || globalFilter.flag != null;
    if (!hasGlobal && !dueFilter) return null;
    return items.filter(i => {
      if (i.stage !== stage) return false;
      if (!_lensMatch(i)) return false;
      if (globalFilter.clients != null && globalFilter.clients.length > 0 && !globalFilter.clients.includes(i.client)) return false;
      if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
      if (globalFilter.flag === "blocked" && !i.is_blocked) return false;
      if (globalFilter.flag === "overdue") {
        const _t = new Date(); _t.setHours(0, 0, 0, 0);
        const _d = parseDate(i.dispatch_due_date || i.due_date);
        if (!(_d && _d < _t)) return false;
      }
      if (dueFilter && !itemMatchesDueFilter(i.dispatch_due_date || i.due_date, dueFilter)) return false;
      return true;
    }).length;
  };

  // ── Division switch (segmented control) ──────────────────────────────────
  // Header brand block from md up, its own row on phones. FLAT track (header
  // redesign, 2-Nov-2026: a track is never pressed in); the thumb wears the
  // division colour + lift ("where you are"), so the active label goes white.
  // Locked-division users get a quiet context chip instead (header only — the
  // phone row keeps its full locked line).
  const lockedDiv = lockedDivision ? divisionByKey(lockedDivision) : null;
  const activeDivColor = divisionLens === "all" ? null : divisionByKey(divisionLens)?.color;
  const divisionSwitch = lockedDiv ? (
    <span
      className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap rl-glass-hairline"
      style={{ color: lockedDiv.color, background: `${lockedDiv.color}14` }}
      title={`Your workspace shows ${lockedDiv.label} items only`}
      data-testid="division-rail-locked"
    >
      <DivisionGlyph division={lockedDiv.key} size={11} withTitle={false} />
      {lockedDiv.label} division
      <Lock className="w-3 h-3 opacity-70" />
    </span>
  ) : (
    <div className="flex items-center" data-testid="division-rail" role="group" aria-label="Division view">
      {/* Pill rail (segmented control, 16-Jul) — RULES:
          · every pill is ALWAYS full-size: icon + label, fixed shape
          · hover changes colour only, never geometry (jitter-proof)
          · one thumb spring-glides between the fixed pills (transform +
            width — moves nothing else), so there is no swap to mis-sequence. */}
      <div className={`relative inline-flex rl-flat-track rounded-full p-[2px] items-center gap-0.5 ${railCooldown ? "rl-rail-cooling" : ""}`}>
        <div
          ref={railThumbRef}
          className={`rl-rail-thumb${activeDivColor ? "" : " all"}`}
          style={activeDivColor ? { backgroundColor: activeDivColor } : undefined}
          aria-hidden="true"
        />
        {[{ key: "all", label: "All", color: null },
          ...PIPELINE_DIVISIONS.map(d => ({ key: d.key, label: d.label, color: d.color }))].map((d) => {
          const active = divisionLens === d.key;
          return (
            <button
              key={d.key}
              ref={(el) => { if (el) railPillRefs.current.set(d.key, el); else railPillRefs.current.delete(d.key); }}
              onClick={() => switchLens(d.key)}
              data-testid={`division-lens-${d.key}`}
              aria-pressed={active}
              aria-label={d.key === "all" ? "All divisions" : `${d.label} division`}
              // The selected pill is one fixed width (fits "Metallic") so the
              // switch is the same size whichever division is named: no gap,
              // and Filter beside it never shifts.
              // Only the SELECTED pill shows its name; the others are quiet
              // icons with an instant tooltip in the division's colour
              // (.rl-div-tip) — Tushar, 2-Nov-2026. Nothing unfolds on hover,
              // so nothing moves under the mouse.
              className={`rl-rail-pill relative z-[1] inline-flex items-center justify-center gap-1.5 h-[22px] rounded-full text-[10.5px] font-semibold whitespace-nowrap shrink-0 transition-[color,opacity] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                active ? "min-w-[76px] px-2.5 text-white" : "group/div w-[26px] text-[hsl(var(--muted-foreground))]"
              }`}
              style={{ "--div": d.color || "hsl(var(--muted-foreground))" }}
            >
              <span
                className={`inline-flex items-center leading-none shrink-0 ${active ? "" : "opacity-60 transition-opacity duration-150 group-hover/div:opacity-100 group-focus-visible/div:opacity-100"}`}
                style={!active && d.color ? { color: d.color } : undefined}
              >
                {d.key === "all"
                  ? <AllDivisionsGlyph size={11} />
                  : <DivisionGlyph division={d.key} size={11} withTitle={false} />}
              </span>
              {active && <span>{d.label}</span>}
              {!active && (
                <span className={`rl-div-tip${d.color ? "" : " all"}`} style={d.color ? { "--tip": d.color } : undefined} role="tooltip" aria-hidden="true">
                  {d.key === "all" ? "All divisions" : `${d.label} only`}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const badge = user ? ROLE_BADGES[user.role] : null;
  const canGoStage = useCallback(
    (stage) => user?.role === 'Admin' || !!(canViewStage && canViewStage(stage)),
    [user?.role, canViewStage]
  );
  const closeShortcuts = useCallback(() => setShortcutsOpen(false), []);
  const showAnnouncement = !!settings?.announcement?.text && settings.announcement.text !== dismissedNotice &&
    (settings.announcement.show_on === "app" || settings.announcement.show_on === "both");
  const appGreeting = look?.greeting && look.greeting.show_on !== "login" ? look.greeting : null;
  const greetingKey = appGreeting ? `${appGreeting.id}|${appGreeting.updated_at}` : "";
  const showGreeting = !!appGreeting && greetingKey !== dismissedGreeting;
  const showNotice = showAnnouncement || showGreeting;
  const closeNotice = (kind) => {
    const isGreeting = kind === "greeting";
    const key = isGreeting ? greetingKey : settings.announcement.text;
    try { localStorage.setItem(isGreeting ? "rl-greeting-closed" : "rl-notice-closed", key); } catch { /* private mode: close for this visit only */ }
    const apply = () => (isGreeting ? setDismissedGreeting(key) : setDismissedNotice(key));
    // The other line stays: just drop this one. Last line: let the drawer
    // slide shut (.rl-notice.is-closing) before unmounting it.
    if (showAnnouncement && showGreeting) { apply(); return; }
    setNoticeClosing(true);
    setTimeout(() => { apply(); setNoticeClosing(false); }, 260);
  };

  return (
    <PipelineTrendContext.Provider value={pipelineTrend}>
    <div
      className="min-h-screen rl-canvas"
      data-testid="app-layout"
      data-scope={divisionLens !== "all" ? divisionLens : undefined}
    >
      {/* Division washes: fixed GPU layers behind the page (index.css .rl-wash). */}
      {PIPELINE_DIVISIONS.map(d => (
        <div key={d.key} className={`rl-wash${divisionLens === d.key ? " on" : ""}`} data-wash={d.key} aria-hidden="true" />
      ))}
      <IconUpdateNotice />
      <MentionNotifBanner />
      {/* Top loading bar */}
      {isRefreshing && (
        <div className="fixed top-0 left-0 right-0 z-[200] h-[2px] pointer-events-none">
          <div className="rl-loading-bar" />
        </div>
      )}
      {/* ── Announcement — only shown when admin has set a message for 'app'
          or 'both'. A slim strip ABOVE the header (the site-wide notice
          spot), so the header and pipeline rail stay one unbroken block.
          3-Nov-2026: styled as a drawer BEHIND the page — recessed and
          shaded, tucked under the header, which gets rounded top corners
          and casts a shadow onto it (.rl-sheet-top). Not sticky: it scrolls
          away, the header stays. */}
      {showNotice && (
        <div className={`rl-notice${noticeClosing ? " is-closing" : ""}`} data-testid="app-notice-drawer" role="status">
          {showGreeting && (() => {
            const a = accentOf(appGreeting.accent);
            return (
              <div
                className="rl-greet max-w-[1440px] mx-auto px-3 md:px-6 min-h-[38px] py-1.5 flex items-center justify-center gap-2.5"
                style={{ "--g-hex": a.hex, "--g-light": a.light, "--g-ink": a.ink }}
                data-testid="app-greeting-bar"
              >
                <span className="rl-notice-well rl-greet-well w-5 h-5 rounded-full flex items-center justify-center shrink-0" aria-hidden="true">
                  <Sparkles className="w-[11px] h-[11px]" strokeWidth={2.4} />
                </span>
                <span className="rl-greet-text min-w-0 text-[12.5px] leading-snug">
                  <strong className="font-semibold">{appGreeting.greeting}</strong>
                  {appGreeting.message && <span className="rl-greet-msg"> · {appGreeting.message}</span>}
                </span>
                <span className="rl-notice-sep shrink-0" aria-hidden="true" />
                <button
                  onClick={() => closeNotice("greeting")}
                  className="rl-notice-close shrink-0"
                  aria-label="Close greeting"
                  title="Close. It stays closed in this browser"
                  data-testid="dismiss-greeting-btn"
                >
                  <XIcon className="w-3 h-3" strokeWidth={2.4} />
                  Close
                </button>
              </div>
            );
          })()}
          {showAnnouncement && (
          <div className={`max-w-[1440px] mx-auto px-3 md:px-6 min-h-[38px] py-1.5 flex items-center justify-center gap-2.5${showGreeting ? " rl-notice-row2" : ""}`} data-testid="app-announcement-bar">
            <span className="rl-notice-well w-5 h-5 rounded-full flex items-center justify-center shrink-0" aria-hidden="true">
              <Megaphone className="w-[11px] h-[11px]" strokeWidth={2.4} />
            </span>
            <span className="rl-notice-text min-w-0 text-[12.5px] font-medium leading-snug">
              {settings.announcement.text}
            </span>
            <span className="rl-notice-sep shrink-0" aria-hidden="true" />
            <button
              onClick={() => closeNotice("announcement")}
              className="rl-notice-close shrink-0"
              aria-label="Close announcement"
              title="Close. It stays closed until there is a new message"
              data-testid="dismiss-announcement-btn"
            >
              <XIcon className="w-3 h-3" strokeWidth={2.4} />
              Close
            </button>
          </div>
          )}
        </div>
      )}
      {/* Header — row 1 is TOOLS (header redesign "Layout 1", 2-Nov-2026):
          logo · brand · scope (division switch + Filter) · magic search ·
          Analyst ·
          bell, mentions, online, history, user · waffle at the far right.
          Depth rule: pressed in = "this button's menu is open"; colour + lift
          = "where you are / switched on". Row 2 is only the pipeline rail. */}
      <header className={`sticky top-0 z-40 ${showNotice ? `rl-sheet-top${pageScrolled || noticeClosing ? " is-stuck" : ""} ` : ""}border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/60 backdrop-blur-md`} data-testid="app-header">
        <div className="max-w-[1440px] mx-auto px-3 md:px-6 h-16 flex items-center gap-2">
          {/* Logo + RockLink brand */}
          <div className="flex items-center gap-3 shrink-0" data-testid="app-logo">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center rounded-md cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              aria-label="Go to dashboard"
              data-testid="logo-home-btn"
            >
              {/* Division-aware logo — the Rockpecker Metallic wordmark by
                  default; on the Drilling lens the Rockpecker Drilling mark
                  crossfades in. The slot SHRINKS-TO-FIT the active logo (wide
                  Metallic ↔ compact Drilling) so the RockLink divider glides in
                  to hug it — no dead gap. Both fades + the width glide run
                  together as one coordinated switch. */}
              <span
                className="relative block h-8 shrink-0 overflow-hidden"
                style={{ width: divisionLens === "drilling" ? "76px" : "160px", transition: "width 450ms ease-out" }}
              >
                <img
                  src={LOGO_URL}
                  alt="Rockpecker Metallic"
                  className="absolute left-0 top-0 h-full w-auto object-contain object-left select-none transition-opacity duration-[450ms] ease-out"
                  style={{ maxWidth: "160px", opacity: divisionLens === "drilling" ? 0 : 1 }}
                  draggable={false}
                />
                <img
                  src={LOGO_URL_DRILLING}
                  alt="Rockpecker Drilling"
                  aria-hidden={divisionLens !== "drilling"}
                  className="absolute left-0 top-0 h-full w-auto object-contain object-left select-none transition-opacity duration-[450ms] ease-out"
                  style={{ maxWidth: "76px", opacity: divisionLens === "drilling" ? 1 : 0 }}
                  draggable={false}
                />
              </span>
            </button>
            <div className="hidden sm:flex flex-col pl-3 border-l border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                <span className="text-lg font-extrabold tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
                </span>
                {/* Version — quiet mono text while seen (no box furniture);
                    flips to the loud gradient pill when a release is unseen.
                    Sync now lives in the online pill on the right. */}
                <button
                  onClick={(e) => { e.stopPropagation(); markReleaseNotesSeen(); setReleaseNotesOpen(true); }}
                  className={`relative inline-flex items-center justify-center transition-all ${
                    hasSeenReleaseNotes
                      ? "text-[10px] font-mono-data font-semibold text-[hsl(var(--muted-foreground))]/60 hover:text-[hsl(var(--foreground))] tracking-wide"
                      : "gap-1 h-[18px] pl-1.5 pr-2 rounded-full text-[10px] font-mono-data font-bold tracking-wide text-[#ea580c] dark:text-orange-400 bg-orange-500/10 border border-orange-500/25 hover:bg-orange-500/15"
                  }`}
                  title={hasSeenReleaseNotes ? "Release notes — v2.6" : "New — tap to see what's changed"}
                  data-testid="version-badge-btn"
                >
                  {/* Unseen release: a quiet orange-tint pill with a small dot
                      (2-Nov-2026 — the old glowing gradient was too loud, and
                      people didn't click it). Seen: plain grey text. */}
                  {!hasSeenReleaseNotes && <span className="w-[5px] h-[5px] rounded-full bg-[#f97316] shrink-0" aria-hidden="true" />}
                  v2.6
                </button>
              </div>
              <p className="text-[9px] font-semibold tracking-widest uppercase leading-none mt-0.5 text-[hsl(var(--muted-foreground))]">
                Production Command Centre
              </p>
            </div>
            {/* Scope — what you're viewing: division + global filter. One line
                with the logo and brand (Tushar, 2-Nov-2026: option A), so
                nothing floats above the wide logo. */}
            {isDesktop && (
              <>
                <span className="w-px h-6 bg-[hsl(var(--border))] shrink-0" aria-hidden="true" />
                <div className="flex items-center gap-1.5" data-testid="header-scope">
                  {divisionSwitch}
                  <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                    <PopoverTrigger asChild>
                      <button
                        className={`relative inline-flex items-center gap-1 h-[26px] pl-2 pr-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-[colors,box-shadow] duration-150 ${
                          activeFilterCount > 0
                            ? "rl-filter-on"
                            : filterPopoverOpen
                              ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                              : "rl-frost-btn text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                        }`}
                        style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}
                        title={activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} active` : "Global filters"}
                        data-testid="global-filter-btn"
                      >
                        <SlidersHorizontal className="w-3 h-3" strokeWidth={2.2} />
                        Filter
                        {activeFilterCount > 0 && (
                          <span className="ml-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-white text-[#ea580c] text-[9.5px] font-bold leading-[15px] text-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </button>
                    </PopoverTrigger>
                <PopoverContent align="start" className="w-[min(360px,92vw)] max-h-[70vh] overflow-auto p-3 rl-frost border-[hsl(var(--border))]">
                  <GlobalFilterContent />
                </PopoverContent>
                  </Popover>
                  {railCooldown && (
                    <span className="rail-switching-note" aria-live="polite">Switching divisions…</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Fixed viewport-centered Command Palette overlay — portalled to document.body */}
          {paletteOpen && createPortal(
            <>
              {/* Floating pill search (2-Nov-2026, option 2): the page dims
                  softly behind a centred pill so the palette never reads as
                  a second header bar. */}
              <div className="fixed inset-0 z-[90] rl-palette-scrim" onClick={() => setPaletteOpen(false)} data-testid="command-palette-backdrop" />
              <div className="fixed left-1/2 -translate-x-1/2 top-3.5 z-[100] w-[min(600px,92vw)]">
                <CommandPalette onClose={() => setPaletteOpen(false)} />
              </div>
              {/* Shortcuts tip — every time search is opened with the mouse
                  on a computer. */}
              {paletteByClick && shortcutLabel && (
                <div className="rl-kbd-tip fixed left-1/2 -translate-x-1/2 bottom-6 z-[100] w-[min(520px,92vw)] flex items-center gap-3 pl-3 pr-2 py-2.5 rounded-2xl" role="note" data-testid="shortcuts-tip">
                  <span className="flex items-center gap-1 shrink-0" aria-hidden="true">
                    <kbd className="rl-kbd-cap">{platform === 'mac' ? '⌘' : 'Ctrl'}</kbd>
                    <kbd className="rl-kbd-cap">K</kbd>
                  </span>
                  <span className="flex-1 min-w-0 text-[12.5px] leading-snug text-[hsl(var(--foreground))]">
                    <span className="font-semibold">Faster next time:</span> press {shortcutLabel} from any page. Single keys jump to stages and filters too.
                  </span>
                  <button
                    onClick={() => { setPaletteOpen(false); setShortcutsOpen(true); }}
                    className="rl-kbd-tip-go h-8 px-3.5 rounded-full text-[12px] font-semibold shrink-0"
                    data-testid="shortcuts-tip-learn"
                  >
                    Learn shortcuts
                  </button>
                  <button
                    onClick={() => setPaletteByClick(false)}
                    className="p-1.5 rounded-full hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))] shrink-0"
                    aria-label="Hide this tip"
                    title="Hide for now"
                    data-testid="shortcuts-tip-close"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </>,
            document.body
          )}

          <KeyboardShortcutsGuide
            open={shortcutsOpen}
            onClose={closeShortcuts}
            mod={platform === 'win' ? 'Ctrl' : '⌘'}
            canGo={canGoStage}
          />

          {/* G-sequence navigation hint overlay */}
          {gHint && gHint.key && createPortal(
            <div
              className={`rl-key-hint${gHint.leaving ? " rl-key-hint--leaving" : ""}`}
              data-testid="g-shortcut-overlay"
              role="status"
              aria-live="polite"
            >
              <kbd key={gHint.seq} className="rl-keycap">{gHint.key}</kbd>
              {gHint.theme ? (
                <div className="rl-theme-rail" data-testid="g-hint-theme-rail">
                  <span className="rl-theme-thumb" style={{ "--i": `${THEME_OPTIONS.findIndex(o => o.key === gHint.theme) * 66}px` }} />
                  {THEME_OPTIONS.map(({ key, icon: Ic, label }) => (
                    <span key={key} className={`rl-theme-seg${key === gHint.theme ? " on" : ""}`}><Ic className="w-[13px] h-[13px]" />{label}</span>
                  ))}
                </div>
              ) : (
                <>
                  {gHint.color && <span className="rl-key-dot" style={{ background: gHint.color }} />}
                  <span className="rl-key-lbl">{gHint.label}</span>
                </>
              )}
              <span key={`bar-${gHint.seq}`} className="rl-key-bar" />
            </div>,
            document.body
          )}

          <div className="flex-1 min-w-0" />

          {/* Magic search (restored 2-Nov-2026) — orange tinted glass like the
              online pill and Analyst; opens the floating pill palette and
              presses in while it's open. */}
          <div className="hidden md:block">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { setPaletteByClick(true); setPaletteOpen(true); }}
                    className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-[colors,box-shadow] duration-150 ${
                      paletteOpen
                        ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                        : "rl-frost-tint rl-tint-orange rl-orange-glass"
                    }`}
                    data-testid="command-launcher-btn"
                    aria-label="Search ⌘K"
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                      className={`absolute transition-opacity duration-500 ease-in-out ${showIdleHint ? 'opacity-0' : 'opacity-100'}`}
                    >
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    </svg>
                    <span
                      aria-hidden={!showIdleHint}
                      className={`absolute font-semibold leading-none transition-opacity duration-500 ease-in-out ${showIdleHint ? 'opacity-100' : 'opacity-0'} ${idleHintLabel && idleHintLabel.length > 3 ? 'text-[8px]' : 'text-[11px]'}`}
                      style={{ fontFamily: "inherit" }}
                    >
                      <span key={idleHintLabel} className="inline-block animate-in fade-in duration-500">{idleHintLabel}</span>
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Search &nbsp;{shortcutLabel || '⌘K'}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {isDesktop && <AnalystButton />}
          <div className="hidden md:block w-px h-6 mx-1 bg-[hsl(var(--border))] shrink-0" aria-hidden="true" />

          {/* Right side controls */}
          <div className="flex items-center gap-2 shrink-0">
            <StageInboxBell />
            <MentionInbox />
            {/* Online/Offline + Live sync status — single pill */}
            <SyncStatusIndicator
              lastRefreshTime={lastRefreshTime}
              isRefreshing={isRefreshing}
              presenceUsers={presenceUsers}
              onPresenceClick={handlePresenceClick}
              onOfflineClick={handleOfflineClick}
              onBackOnline={handleBackOnline}
              onSync={handleHeaderSync}
              syncBusy={refreshing || isRefreshing}
              justRefreshed={justRefreshed}
            />

            {/* ── Desktop cluster (>=md) ──────────────────────────────── */}
            <div className="hidden md:flex items-center gap-2">
              {/* History Log — direct icon (formerly inside the ⋯ menu) */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate(location.pathname === '/changelog' ? '/' : '/changelog')}
                      className={`flex items-center justify-center w-8 h-8 rounded-full transition-[transform,colors,box-shadow] duration-150 ${
                        location.pathname === '/changelog'
                          ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                          : "rl-frost-btn text-[hsl(var(--muted-foreground))]"
                      }`}
                      data-testid="header-history-log"
                      aria-label={location.pathname === '/changelog' ? "Back to dashboard" : "History Log"}
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent><p>{location.pathname === '/changelog' ? "Back to dashboard" : "History Log"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* User menu */}
              {user && (
                <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full text-[hsl(var(--muted-foreground))] transition-[colors,box-shadow] duration-150 ${
                        userMenuOpen
                          ? "bg-zinc-200 dark:bg-zinc-800 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                          : "rl-frost-btn"
                      }`}
                      data-testid="user-menu-btn"
                      aria-label="User menu"
                    >
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#ef4444] to-[#f97316] text-white text-[10px] font-bold shrink-0">
                        {user.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-[hsl(var(--foreground))]">{user.name.split(" ")[0]}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 rl-frost">
                    <div className="px-3 py-2.5">
                      <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{user.name}</p>
                      {badge && (
                        <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.color}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    <ThemeSlider themePref={themePref} setThemePref={setThemePref} />
                    <FlatModeRow flatMode={flatMode} toggleFlat={toggleFlat} />
                    <EasyReadRow easyRead={easyRead} toggleEasyRead={toggleEasyRead} />
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => setAdminSettingsOpen(true)} className="gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Admin settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="gap-2 cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10"
                    >
                      <LogOut className="w-4 h-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Waffle — far right, after the user menu (people look for it
                  on the right). Presses in while open; never orange. */}
              {isDesktop && waffle}
            </div>

            {/* ── Mobile overflow menu (<md) ──────────────────────────── */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 rounded-md hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))]"
                    data-testid="header-mobile-menu"
                    aria-label="More options"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rl-frost">
                  {/* Non-interactive user info header */}
                  {user && (
                    <div className="px-3 py-2.5 border-b border-[hsl(var(--border))]">
                      <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{user.name}</p>
                      {badge && (
                        <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.color}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                  )}
                  <DropdownMenuItem onClick={() => setPaletteOpen(true)} className="gap-2 cursor-pointer">
                    <Search className="w-4 h-4" />
                    Search
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShortcutsOpen(true)} className="gap-2 cursor-pointer" data-testid="menu-keyboard-shortcuts">
                    <Keyboard className="w-4 h-4" />
                    Keyboard shortcuts
                    <span className="ml-auto text-[11px] text-[hsl(var(--muted-foreground))]">?</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <ThemeSlider themePref={themePref} setThemePref={setThemePref} />
                  <FlatModeRow flatMode={flatMode} toggleFlat={toggleFlat} />
                    <EasyReadRow easyRead={easyRead} toggleEasyRead={toggleEasyRead} />
                  <DropdownMenuItem onClick={() => setFilterOpen(true)} className="gap-2 cursor-pointer">
                    <SlidersHorizontal className="w-4 h-4" />
                    Global Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-auto text-[10px] font-bold text-orange-500">{activeFilterCount} active</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/changelog")} className="gap-2 cursor-pointer">
                    <History className="w-4 h-4" />
                    History Log
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => setAdminSettingsOpen(true)} className="gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" />
                      Admin Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="gap-2 cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10"
                  >
                    <LogOut className="w-4 h-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Row 2 — the pipeline rail, and only the rail (md+). On phones: the
          icon Dashboard button + the inline stage selector + the waffle, then
          the division switch + Analyst row (unchanged from before). */}
      <nav className="border-b border-[hsl(var(--border))]" data-testid="pipeline-nav">
        {isDesktop ? (
          <div className="max-w-[1440px] mx-auto px-6 pt-3 pb-3.5">
            <PipelineRail
              stages={visibleNav}
              pathname={location.pathname}
              onNavigate={(to) => navigate(to)}
              lensItems={lensItems}
              getStageCount={getStageCount}
              getFilteredStageCount={getFilteredStageCount}
              reworkCount={reworkCount}
              lastAdvanceInfo={lastAdvanceInfo}
              showValue={!!(canDo && canDo("can_view_value"))}
              trend={pipelineTrend}
              reduced={railReduced}
            />
          </div>
        ) : (
          <div className="max-w-[1440px] mx-auto px-3 pt-2 pb-3">
            <div className="flex items-center gap-2">
            {(() => {
              const d = visibleNav.find(s => s.path === '/');
              if (!d) return null;
              const dActive = location.pathname === '/';
              const DIcon = d.icon;
              return (
                <button
                  onClick={() => navigate('/')}
                  data-testid="nav-dashboard"
                  className={`inline-flex items-center justify-center gap-1.5 w-[42px] px-0 h-[42px] rounded-md text-[13px] font-semibold shrink-0 transition ${dActive ? 'text-white rl-dash-glass' : 'rl-nav-glass text-[hsl(var(--muted-foreground))]'}`}
                  style={{ fontFamily: "var(--rl-font-heading), sans-serif", ...(dActive ? { backgroundColor: '#fbbf24', backgroundImage: 'radial-gradient(60% 130% at 20% 30%, #a855f7 0%, transparent 55%), radial-gradient(70% 130% at 75% 25%, #ef4444 0%, transparent 55%), radial-gradient(80% 140% at 55% 95%, #ec4899 0%, transparent 55%), radial-gradient(70% 130% at 10% 90%, #38bdf8 0%, transparent 55%), radial-gradient(60% 120% at 90% 85%, #f59e0b 0%, transparent 55%)', textShadow: '0 1px 2px rgba(0,0,0,0.3)' } : {}) }}
                >
                  <DIcon className="w-4 h-4" />
                </button>
              );
            })()}
            {/* Mobile stage selector (16-Jul) — sits INLINE in the gap between
                Dashboard and the waffle (flex-1); the carved track is hidden
                below md. Compact so the widest label ("Manufacturing") fits;
                label truncates gracefully if ever tighter. Stock Center
                mobile-switcher language (pill trigger + rl-frost stage list). */}
            <div className="md:hidden flex-1 min-w-0">
              {(() => {
                const stageNav = visibleNav.filter(s => s.path !== '/');
                const activeStage = stageNav.find(s => s.path === location.pathname);
                const ActiveIcon = activeStage ? activeStage.icon : null;
                const activeCount = activeStage ? getStageCount(activeStage.stage) : null;
                return (
                  <DropdownMenu open={stageMenuOpen} onOpenChange={setStageMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="stage-select-mobile"
                        aria-label="Choose stage"
                        className={`relative w-full rounded-full h-[42px] px-8 inline-flex items-center justify-center gap-2.5 text-[13px] font-semibold text-[hsl(var(--foreground))] transition-[box-shadow,background-color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                          stageMenuOpen
                            ? "bg-[hsl(var(--muted))]/60 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                            : "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))]"
                        }`}
                      >
                        <span className="shrink-0 flex items-center justify-center" style={activeStage ? { color: activeStage.color } : undefined}>
                          {ActiveIcon
                            ? <ActiveIcon className="w-[18px] h-[18px]" />
                            : <PipelineRibbonIcon className="w-6 h-4" />}
                        </span>
                        <span className="min-w-0 truncate">{activeStage ? activeStage.label : "Pipeline"}</span>
                        {activeCount !== null && activeStage && (
                          <span className="rl-carved font-mono-data tabular-nums text-[11px] px-1.5 py-0.5 rounded-full shrink-0"
                                style={{ background: `${activeStage.color}1f`, color: activeStage.color }}>
                            {activeCount}
                          </span>
                        )}
                        <ChevronDown className={`absolute right-3 w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform duration-200 ${stageMenuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" sideOffset={6} collisionPadding={16} className="rl-frost border-[hsl(var(--border))] rounded-2xl w-[var(--radix-dropdown-menu-trigger-width)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-auto p-1.5">
                      {stageNav.map((s, i) => {
                        const isActive = s.path === location.pathname;
                        const c = getStageCount(s.stage);
                        const SIcon = s.icon;
                        // Rested-state sequence cue (29-Jul): a faint chevron centred
                        // between every pair of rows, so the menu reads as a downward
                        // spine PO Received -> Completed regardless of which stage is
                        // selected. Purely decorative: aria-hidden + pointer-events-none
                        // so it never eats a tap.
                        const connector = i < stageNav.length - 1 ? (
                          <div key={`${s.path}-conn`} aria-hidden="true" className="h-3 flex items-center justify-center pointer-events-none select-none">
                            <ChevronDown className="w-3 h-3 text-[hsl(var(--foreground))]/20" strokeWidth={1.8} />
                          </div>
                        ) : null;
                        return (
                          <React.Fragment key={`${s.path}-grp`}>
                          <DropdownMenuItem key={s.path} onClick={() => navigate(s.path)} data-testid={`stage-opt-${s.path.replace('/', '')}`}
                            className="flex items-center justify-center gap-2.5 h-11 px-2.5 rounded-lg cursor-pointer"
                            style={isActive ? { background: `${s.color}14` } : undefined}>
                            {/* Variant D: icon + label + count are ONE centred cluster,
                                so the connector chevrons form a true centre spine.
                                No trailing tick — it would shift the active row's
                                cluster off-centre; active is carried by the colour
                                wash, the bold label and the tinted count chip. */}
                            <span className="shrink-0 flex items-center justify-center" style={{ color: s.color }}>
                              <SIcon className="w-[18px] h-[18px]" />
                            </span>
                            <span className={`text-[14px] truncate ${isActive ? "font-semibold text-[hsl(var(--foreground))]" : "font-medium text-[hsl(var(--foreground))]/90"}`}>{s.label}</span>
                            {c !== null && (
                              <span className="rl-carved font-mono-data tabular-nums text-[11px] px-1.5 py-0.5 rounded-full shrink-0"
                                    style={isActive ? { background: `${s.color}1f`, color: s.color } : { background: "hsl(var(--muted) / 0.7)", color: "hsl(var(--muted-foreground))" }}>
                                {c}
                              </span>
                            )}
                          </DropdownMenuItem>
                          {connector}
                          </React.Fragment>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            </div>
              {waffle}
            </div>
            <div className="flex items-center gap-2 mt-2">
              {lockedDivision ? (
                <div
                  className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] rl-glass-hairline"
                  style={{ color: ld.color, background: `${ld.color}14` }}
                  data-testid="division-rail-locked"
                >
                  <DivisionGlyph division={ld.key} size={12} withTitle={false} />
                  <span className="font-semibold">{ld.label} division</span>
                  <span className="opacity-75">· your workspace shows {ld.label} items only</span>
                  <Lock className="w-3 h-3 ml-auto opacity-70" />
                </div>
              ) : (
                <>
                  {divisionSwitch}
                  {railCooldown && (
                    <span className="rail-switching-note" aria-live="polite">Switching divisions…</span>
                  )}
                </>
              )}
              <span className="flex-1" />
              <AnalystButton />
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-[1440px] mx-auto px-3 sm:px-6 py-4 sm:py-6 overflow-x-hidden" data-testid="main-content">
        <Outlet />
      </main>

      {/* App Footer */}
      <footer className="border-t border-[hsl(var(--border))]/60 mt-auto" style={{ background: "hsl(var(--background))" }}>
        <div className="max-w-[1440px] mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
            Built with <span className="text-red-400">♥</span> by{" "}
            <span className="font-semibold text-[hsl(var(--foreground))]/60">Rockpecker</span>{" "}
            <span>🇮🇳</span>
            <span className="mx-2 opacity-30">·</span>
            <span className="opacity-60">Production Command Centre</span>
            <span className="mx-2 opacity-30">·</span>
            <span className="opacity-40">© 2026</span>
          </p>
          <div className="flex items-center gap-2">
            <InstallAppLink className="px-2.5 py-1 rounded-md text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-orange-400 hover:!opacity-100" />
            <button
              onClick={() => setReportOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors"
              data-testid="report-problem-btn"
            >
              <MessageSquare className="w-3 h-3" />
              Report a Problem
            </button>
          </div>
        </div>
      </footer>

      {/* Global Filter Panel */}
      <GlobalFilterPanel open={filterOpen} onClose={() => setFilterOpen(false)} />

      {/* Admin Settings Panel */}
      <AdminSettingsPanel open={adminSettingsOpen} onClose={() => setAdminSettingsOpen(false)} />

      {/* PWA install button is in the footer below */}

      {/* Report a Problem Modal */}
      {reportOpen && (
        <Dialog open onOpenChange={(open) => { if (!open) setReportOpen(false); }}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md p-0 overflow-hidden" data-testid="report-problem-modal">
            <div className="h-1 w-full bg-gradient-to-r from-red-500 to-orange-500" />
            <div className="px-6 pt-5 pb-2">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Report a Problem</p>
                    <p className="text-xs font-normal text-[hsl(var(--muted-foreground))]">Sent to the RockLink support team</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
            </div>
            <div className="px-6 py-3 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] block mb-1.5">Category</label>
                <select
                  value={reportForm.category}
                  onChange={e => setReportForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="report-category-select"
                >
                  <option>Bug Report</option>
                  <option>Feature Request</option>
                  <option>General Feedback</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] block mb-1.5">Description</label>
                <textarea
                  value={reportForm.description}
                  onChange={e => setReportForm(f => ({ ...f, description: e.target.value }))}
                  rows={5}
                  placeholder="Describe the issue or suggestion..."
                  className="w-full px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="report-description-textarea"
                />
              </div>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
                Submitted as <span className="font-semibold text-[hsl(var(--foreground))]">{user?.name}</span> · {user?.role}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-[hsl(var(--border))]/50 flex items-center justify-between">
              <button
                onClick={() => setReportOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]"
                data-testid="report-cancel-btn"
              >Cancel</button>
              <button
                onClick={handleReportSubmit}
                disabled={reportSending || !reportForm.description.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
                data-testid="report-submit-btn"
              >
                {reportSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                Submit Report
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Offline Warning Dialog */}
      <Dialog open={offlineDialogOpen} onOpenChange={setOfflineDialogOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-red-500/30 max-w-sm p-0 overflow-hidden" data-testid="offline-dialog">
          <div className="h-1 w-full bg-red-500" />
          <div className="px-6 pt-5 pb-2">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5 text-[hsl(var(--foreground))]">
                <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                  <WifiOff className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-base font-bold">Connection Lost</p>
                  <p className="text-xs font-normal text-red-400 mt-0.5">Server unreachable</p>
                </div>
              </DialogTitle>
              <DialogDescription className="mt-4 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
                Cannot establish a connection with the RockLink server. Any changes you make now{" "}
                <strong className="text-[hsl(var(--foreground))]">may not be saved</strong>.
                <br /><br />
                Please check your network connection and wait for the status indicator to return{" "}
                <span className="text-green-400 font-semibold">Online</span>{" "}
                before continuing your work.
              </DialogDescription>
            </DialogHeader>
          </div>
          <DialogFooter className="px-6 pb-5 pt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
              <span className="w-2 h-2 rounded-full bg-red-500 offline-dot inline-block" />
              Checking every 10s...
            </div>
            <button
              onClick={() => setOfflineDialogOpen(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]"
              data-testid="offline-dismiss-btn"
            >
              I Understand
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Who's online — presence lightbox */}
      <Dialog open={presenceOpen} onOpenChange={setPresenceOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[520px] max-w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="presence-dialog">
          <DialogTitle className="sr-only">Team presence</DialogTitle>
          <DialogDescription className="sr-only">Live view of who's currently active in RockLink and when everyone else last logged in.</DialogDescription>
          <PresenceList users={presenceUsers} loading={presenceLoading} />
        </DialogContent>
      </Dialog>

      {/* Release Notes — v1.8.8 */}
      <Dialog open={releaseNotesOpen} onOpenChange={setReleaseNotesOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[720px] max-w-[95vw] max-h-[88vh] overflow-y-auto" data-testid="release-notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-orange-500" />
              <span>What's new in</span>
              <span className="font-extrabold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
              </span>
              <span className="px-2 py-0.5 rounded text-xs font-bold tracking-wider bg-orange-500/15 text-orange-500 border border-orange-500/30">v2.6</span>
            </DialogTitle>
            <DialogDescription>
              A fresh coat for RockLink — a neater, calmer look that's easier to read and quicker to get around.
            </DialogDescription>
          </DialogHeader>
          <ReleaseNotesBody />
        </DialogContent>
      </Dialog>
    </div>
    </PipelineTrendContext.Provider>
  );
}

/* ── Presence list ───────────────────────────────────────────────────────── */
function fmtRelative(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const PRESENCE_ROLE_COLOR = { Admin:"#ef4444", Received:"#3b82f6", Procurement:"#f97316", Manufacturing:"#a855f7", QC:"#14b8a6", Dispatch:"#f59e0b", Viewer:"#64748b" };

function fmtPresenceTime(iso) {
  const d = new Date(iso); if (isNaN(d)) return "";
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 180) return diffMin < 60 ? `${diffMin}m ago` : `${Math.floor(diffMin / 60)}h ago`;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const hh = String(d.getHours()).padStart(2,"0"), mm = String(d.getMinutes()).padStart(2,"0");
  const startToday = new Date(now); startToday.setHours(0,0,0,0);
  const startYest = new Date(startToday); startYest.setDate(startToday.getDate() - 1);
  if (d >= startToday) return `${hh}:${mm}`;
  if (d >= startYest) return `Yesterday ${hh}:${mm}`;
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]} ${hh}:${mm}`;
}

function PresenceList({ users, loading, compact = false }) {
  const [q, setQ] = React.useState("");
  const [searchOpen, setSearchOpen] = React.useState(false);
  if (loading && users.length === 0) {
    return (<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div>);
  }
  const ql = q.trim().toLowerCase();
  const match = (u) => !ql || (u.name || u.email || "").toLowerCase().includes(ql) || (u.role || "").toLowerCase().includes(ql);
  const online = users.filter(u => u.is_online && match(u));
  const offline = users.filter(u => !u.is_online && match(u));
  const startToday = new Date(); startToday.setHours(0,0,0,0);
  const onlineTotal = users.filter(u => u.is_online).length;
  // "Active today" = anyone seen since midnight (heartbeat OR login), not just
  // those who freshly re-authenticated today. last_seen_at bumps on every
  // presence ping, so this also counts carried-over sessions. Fall back to
  // last_login_at for the edge case of a login with no heartbeat yet.
  const activeToday = users.filter(u => {
    const ts = u.last_seen_at || u.last_login_at;
    return ts && new Date(ts) >= startToday;
  }).length;
  const closeSearch = () => { setSearchOpen(false); setQ(""); };

  // Compact popover variant: tight header, stats line, and the online list only
  // (no search toggle, no "recently active" offline section). Keeps the pill's
  // dropdown small and focused on who's here now.
  if (compact) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <Users className="w-4 h-4 shrink-0 text-emerald-500" />
          <span className="text-sm font-semibold text-[hsl(var(--foreground))] flex-1">Team presence</span>
        </div>
        <div className="flex gap-4 mb-3">
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]"><span className="font-mono-data font-bold text-emerald-500">{onlineTotal}</span> Online</span>
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]"><span className="font-mono-data font-bold text-[hsl(var(--foreground))]">{activeToday}</span> Active today</span>
        </div>
        <div className="space-y-3">
          <Section title="Online now" count={online.length} color="emerald">
            {online.length === 0 && (<div className="text-xs text-[hsl(var(--muted-foreground))] italic py-2">Just you so far.</div>)}
            {online.map(u => <PresenceRow key={u.id} user={u} />)}
          </Section>
          {offline.length > 0 && (
            <Section title="Recently active" count={offline.length} color="slate">
              {offline.map(u => {
                const la = u.last_seen_at || u.last_login_at || "";
                return (
                  <div key={u.id} className="flex items-center justify-between gap-2 py-1 px-2 text-xs">
                    <span className="truncate text-[hsl(var(--foreground))]">{u.name || u.email}</span>
                    <span className="shrink-0 font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]">{la ? fmtPresenceTime(la) : "Never"}</span>
                  </div>
                );
              })}
            </Section>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 min-h-[28px] pr-8">
        {searchOpen ? (
          <div className="flex items-center gap-2 flex-1 h-8 px-2.5 rounded-lg border border-[hsl(var(--border))] focus-within:ring-2 focus-within:ring-orange-500/30">
            <Search className="w-3.5 h-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") closeSearch(); }} placeholder="Search teammates…" data-testid="presence-search"
              className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none" />
            <button type="button" onClick={closeSearch} aria-label="Close search" className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Users className="w-5 h-5 shrink-0 text-emerald-500" />
            <span className="text-lg font-semibold text-[hsl(var(--foreground))] flex-1">Team presence</span>
            <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search teammates" data-testid="presence-search-toggle"
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors">
              <Search className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      <div className="flex gap-5 mt-3 mb-3">
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]"><span className="font-mono-data font-bold text-emerald-500">{onlineTotal}</span> Online</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]"><span className="font-mono-data font-bold text-[hsl(var(--foreground))]">{activeToday}</span> Active today</span>
      </div>
      <div className="space-y-4">
        <Section title="Online now" count={online.length} color="emerald">
          {online.length === 0 && (<div className="text-xs text-[hsl(var(--muted-foreground))] italic py-2">{ql ? "No online match." : "Just you so far."}</div>)}
          {online.map(u => <PresenceRow key={u.id} user={u} />)}
        </Section>
        <Section title="Recently active" count={offline.length} color="slate">
          {offline.length === 0 && (<div className="text-xs text-[hsl(var(--muted-foreground))] italic py-2">{ql ? "No match." : "Everyone's online."}</div>)}
          {offline.map(u => <PresenceRow key={u.id} user={u} />)}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, count, color, children }) {
  const map = {
    emerald: "text-emerald-500",
    slate:   "text-[hsl(var(--muted-foreground))]",
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-widest ${map[color]}`}>{title}</span>
        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">{count}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function PresenceRow({ user }) {
  const initials = (user.name || user.email).split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const c = PRESENCE_ROLE_COLOR[user.role] || "#64748b";
  const lastActive = (() => { const a = user.last_seen_at || ""; const b = user.last_login_at || ""; if (!a && !b) return ""; if (!a) return b; if (!b) return a; return a > b ? a : b; })();
  const dotColor = user.is_online
    ? "text-emerald-500 fill-emerald-500"
    : "text-[hsl(var(--muted-foreground))]/50 fill-[hsl(var(--muted-foreground))]/30";
  const timeLabel = user.is_online ? "Online" : (lastActive ? fmtPresenceTime(lastActive) : "Never");
  return (
    <div className="flex items-center gap-2.5 py-1 px-2 rounded-md hover:bg-[hsl(var(--muted))]/40 transition-colors" data-testid={`presence-row-${user.id}`}>
      <div className="relative shrink-0">
        <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center font-semibold text-[10px]" style={{ background: `${c}1f`, color: c, border: `0.5px solid ${c}40` }}>
          {initials || "?"}
        </div>
        <Circle className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${dotColor} stroke-[hsl(var(--card))] stroke-2`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 leading-tight">
          <span className="text-[13px] font-medium text-[hsl(var(--foreground))] truncate">{user.name || user.email}</span>
          {user.is_me && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded bg-orange-500/15 text-orange-500 shrink-0">You</span>}
          {!user.is_active && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] shrink-0">Deactivated</span>}
        </div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate leading-tight mt-0.5">{user.role}</div>
      </div>
      <span className="shrink-0 font-mono-data text-[11px] tabular-nums" style={{ color: user.is_online ? "#059669" : "hsl(var(--foreground) / 0.7)" }}>
        {timeLabel}
      </span>
    </div>
  );
}


/* ── Compact v1.3 benefit bullet ─────────────────────────────────────────── */
function RN13Item({ icon: Icon, accent, title, children }) {
  return (
    <div className="flex gap-3" data-testid={`rn13-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div
        className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center mt-0.5"
        style={{ backgroundColor: accent + "1a", color: accent }}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[hsl(var(--foreground))] leading-snug">{title}</p>
        <p className="text-[13px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">{children}</p>
      </div>
    </div>
  );
}

/* ── Release notes body (v1.3 concise, v1.2 collapsed) ───────────────────── */
function ReleaseNoteSection({ icon: Icon, accent, title, children }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-4" data-testid={`rn-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accent }}>{title}</span>
      </div>
      <div className="text-sm text-[hsl(var(--foreground))] leading-relaxed space-y-1.5">{children}</div>
    </div>
  );
}

function ReleaseNotesBody() {
  const [showPrev, setShowPrev] = useState(false);
  return (
    <div className="space-y-5 mt-2" data-testid="release-notes-body">
      {/* ── v2.6 ── (3-Nov-2026: visual overhaul, kept light on detail) */}
      <div className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-orange-500">What's new · v2.6</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">A neater, cleaner RockLink.</span>
        </div>

        <RN13Item icon={Sparkles} accent="#f97316" title="A fresh look, top to bottom">
          The header, the pipeline and the dashboard have been tidied up — less clutter, clearer numbers, and everything where you'd expect it.
        </RN13Item>

        <RN13Item icon={LayoutDashboard} accent="#a855f7" title="Your pipeline at a glance">
          Every stage now sits in one row of arrows with its count, value and a small 7-day trend. Tap any stage to go straight there.
        </RN13Item>

        <RN13Item icon={Keyboard} accent="#0ea5e9" title="Get around faster with your keyboard">
          Press Shift + / (the ? key) to see every shortcut and try them out. It's also under your name menu → Keyboard shortcuts.
        </RN13Item>
      </div>

      {/* ── v2.5 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.5</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Meet Analyst — ask RockLink anything and get the answer worked out.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={AnalystGlyph} accent="#f97316" title="Analyst — ask RockLink anything">
          A new page, behind the Analyst button under the menu grid. Ask in plain English — what's late, what's stuck, what shipped, who did what — and Analyst works through your live RockLink data, open, shipped and completed, with its history. Numbers are calculated, never guessed, and every answer can show how it was worked out. Quick questions answer in seconds; one-click suggestions cover the regulars. For those it's switched on for.
        </RN13Item>

        <RN13Item icon={Briefcase} accent="#0ea5e9" title="Customer sheets that are safe to send">
          Order status, late-line updates, delivery plans, expedite lists and call briefs for any client — built only from what that customer may see. No values, costs, internal notes or names ever reach them.
        </RN13Item>

        <RN13Item icon={FileSpreadsheet} accent="#10b981" title="Excel, PDF and email in one click">
          Every table copies cleanly into Excel or Outlook and downloads as a formatted sheet. Any answer becomes a Rockpecker-branded PDF, or goes by email to Rockpecker colleagues with the PDF attached. Save an answer as a report and re-run it on fresh data whenever you need it.
        </RN13Item>

        <RN13Item icon={EyeOff} accent="#64748b" title="The History Log keeps money private">
          If your role doesn't show order value or spend, those amounts and spend entries no longer appear in the History Log or Live Activity either.
        </RN13Item>

        <RN13Item icon={Gauge} accent="#f97316" title="The History Log opens in a moment">
          With over 30,000 entries it had grown slow to open. It now loads just the page you're looking at, so it opens almost straight away — filters, paging and export work as before.
        </RN13Item>

        <RN13Item icon={MailCheck} accent="#2F6DAE" title="One Daily Digest, not two">
          The 12:00 Daily Digest and the other scheduled emails now go out exactly once.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          Analyst runs on Anthropic's Claude through Rockpecker's own account — your data is not used to train AI models.
        </p>
      </div>

      {/* ── v2.4 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.4</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Blocking starts naming who must act — plus rupees in Stores and a faster word from Maintenance.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={ShieldAlert} accent="#e11d48" title="Blocking asks one question: who are we waiting on?">
          Reporting a problem now starts with the stakeholder who has to act — the customer, a supplier, a lab, a job-work vendor, or a department inside the factory — and only then the reason, from that owner's own short list for the stage you're on. Sub-teams disappear as a step; the owner carries that meaning. The old codes still work, and every historic block has been mapped across.
        </RN13Item>

        <RN13Item icon={Hourglass} accent="#f59e0b" title="One line can wait on more than one person">
          A line held up by both a late supplier and a machine queue no longer has to pick one. Each block is raised and cleared on its own, the row wears a chip for each, and the line only comes unblocked when the last one clears.
        </RN13Item>

        <RN13Item icon={CalendarClock} accent="#3b82f6" title="A promise date, and a record of who keeps it">
          Anything waiting on someone outside the factory needs a date they committed to. The date rides on the row, turns red the moment it slips, and quietly builds a promise-keeping record for each supplier and customer.
        </RN13Item>

        <RN13Item icon={LayoutGrid} accent="#e11d48" title="The Blocked Board — the weekly review, in one page">
          Every open block in the pipeline, external owners first and oldest first: who we're waiting on, how long, what was promised, and which promises have already slipped. Aging buckets, filters by owner and stage, and blocked-days by party for the conversations that follow. Under Modules, for those granted it.
        </RN13Item>

        <RN13Item icon={Boxes} accent="#f97316" title="Assemblies and the Child Part Tracker">
          In PO Received, mark a line as an Assembly and paste in its child parts — the preview shows exactly what will be saved. Material then tracks every part: supplier, ETA, landed cost in ₹ or $ (counted in spend in dollars), and MTC. Revising an ETA alerts the people on the list. An assembly can move to Manufacturing once some parts are in, wearing an orange Partially procured flag until the last one arrives.
        </RN13Item>

        <RN13Item icon={IndianRupee} accent="#10b981" title="Stores speaks rupees">
          The cost box in Stores now means ₹ — which is what everyone was typing into it anyway. Receiving or editing a lot shows the ₹ sign right in the box, with a ₹/$ flip beside it for the rare dollar invoice. Nothing already entered has changed; it's simply labelled for what it always was.
        </RN13Item>

        <RN13Item icon={Mail} accent="#2F6DAE" title="Maintenance hears the moment a machine stops">
          When a machine is reported stopped, the people on the Maintenance alert list can get an email straight away. Whoever reports it sees who's being told, and can leave someone out or add a colleague for that one breakdown. A machine still running with a problem never sends an email.
        </RN13Item>

        <RN13Item icon={Glasses} accent="#a855f7" title="Easy-read text, for long sessions">
          A new switch in your user menu swaps in rounder, more open letters and lifts the smallest text a touch — easier on tired eyes across a full shift. It's yours alone, and off unless you turn it on.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          …plus Owner, Party and Promise columns on the export, and the History Log naming the owner on every block.
        </p>
      </div>

      {/* ── v2.3 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.3</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Machines get their own module — and Ecotile comes online.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={Wrench} accent="#2F6DAE" title="Maintenance — machine breakdowns, start to finish">
          Tap the machine that has a problem, pick what's wrong, send. Maintenance takes it up, marks it repaired, and production signs it back as working — the machine is only closed when the people who run it say so. Every breakdown carries its own clock and, where a machine has an hourly rate, what the downtime cost.
        </RN13Item>

        <RN13Item icon={Calendar} accent="#10b981" title="Every machine's service rhythm">
          Preventive maintenance lives here too: set how often a machine should be serviced, log the service with a tap for each job done, and the next one schedules itself. Overdue machines sort to the top and carry a count on the nav.
        </RN13Item>

        <RN13Item icon={Package} accent="#3b82f6" title="Spares, and the small stuff that gets forgotten">
          Pause a repair for parts and log the order in the same step — it stays linked to that breakdown until it arrives. Separately, anything that isn't a breakdown but shouldn't be forgotten — a brighter task light, a loose guard latch — gets raised, taken up and resolved on its own board.
        </RN13Item>

        <RN13Item icon={Mail} accent="#f59e0b" title="It tells you when a machine stays down">
          A machine stopped for more than four hours emails whoever is on the list, once. And on the 1st of every month the maintenance report — breakdowns, downtime, what it cost, the machines that break most — arrives as a PDF, or downloads on demand.
        </RN13Item>

        <RN13Item icon={LayoutGrid} accent="#14b8a6" title="Ecotile — the flooring division comes online">
          Ecotile now has its own portal inside RockLink: orders, payments against them, monthly targets, finished-goods and raw-material stores, and CSV import for the whole book. The spreadsheet the division ran on is retired.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          …plus a wrench badge on the shop-floor view the moment a machine is reported down, a rebuilt sign-in page, keycap hints across the app with a gliding Light · Auto · Dark rail on <b>N</b>, a mobile stage selector, and steadier live indicators everywhere.
        </p>
      </div>

      {/* ── v2.2 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.2</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Material sourcing grows up, and scrap earns its ledger.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={Warehouse} accent="#10b981" title="Material sourcing — stores or order, your choice">
          Marking Material as Ordered now starts with a simple question: use what's already in Stores, or order new — or both. Reserve stock right there and its cost lands on the line's spend history by itself; a green pill on the row brings you back to the full picture anytime. When everything's sourced, one tap marks the material Complete.
        </RN13Item>

        <RN13Item icon={Recycle} accent="#f97316" title="Scrap sales, in their own ledger">
          Scrap sales now have a proper home: log each sale in USD, import a whole sheet by CSV, and see scrap revenue take its place on the dashboard next to dispatches — instead of living in a side spreadsheet.
        </RN13Item>

        <RN13Item icon={Rows} accent="#3b82f6" title="Stage rows, tightened up">
          Enter several ERP codes in one go, read status from a single unified column, and fit more lines on screen in compact mode — the busiest pages, faster to work.
        </RN13Item>

        <RN13Item icon={Activity} accent="#14b8a6" title="Know whose part moved, at a glance">
          Every line in the Live Activity feed now carries the client's mark in quiet grey — scan the feed and know whose work is moving without reading a word.
        </RN13Item>

        <RN13Item icon={Link2} accent="#a855f7" title="Spot a part that lives in two places">
          When a part number is also active in another stage, a small marker says so right on the row — hover it to see where, so nobody machines against the wrong line.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          …plus carved identifier chips across the Changelog, dashboard and Stock Center, roomier dialogs that stay tidy with long part names, and dozens of small refinements.
        </p>
      </div>

      {/* ── v2.1 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.1</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Stock Center arrives, plus a clearer shop floor.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={Boxes} accent="#f97316" title="Stock Center — your drill-bit warehouse, online">
          Rockpecker Drilling's stock now lives inside RockLink. Find any bit by type, size and thread instead of scrolling a spreadsheet, see what's on hand across all five warehouses, and read its carrying value at a glance. A whole portal, built for the warehouse team.
        </RN13Item>

        <RN13Item icon={ArrowLeftRight} accent="#534AB7" title="Move bits between warehouses">
          Send a bit from one warehouse to another in two clear steps: the source sends it, the destination confirms it arrived. An &ldquo;Incoming transfers&rdquo; strip shows what's on its way to you, and the price travels in its original currency.
        </RN13Item>

        <RN13Item icon={SlidersHorizontal} accent="#3b82f6" title="Advanced actions — do more at once">
          Pick one bit or a whole batch of serials and act on them together: transfer, change shelf or status, export, or sell. Select with a tap or the keyboard, and the actions that don't apply right now tell you why — so nothing's a guess.
        </RN13Item>

        <RN13Item icon={FileDiff} accent="#10b981" title="Smarter import — see exactly what changes">
          Re-import a corrected sheet and RockLink shows you a preview first: what's new, what's changing, and what stays the same — before anything is written. Updates only touch spec details, never a recorded sale. The end of stale data from old spreadsheets.
        </RN13Item>

        <RN13Item icon={Monitor} accent="#0891b2" title="The shop floor, live in RockLink">
          See your machines on the Manufacturing stage — a real-time view of what's running on the floor, right where the work is. (Rolling out in stages; the live 24/7 feed comes next.)
        </RN13Item>

        <RN13Item icon={ShieldAlert} accent="#e11d48" title="Every stage, clearer to read">
          A self-evident pass across the stage pages: blocked rows now explain why on hover, action icons carry labels, and the Notes column lines up cleanly. Less hunting, fewer questions — built so a new hand can use it without a walkthrough.
        </RN13Item>

        <RN13Item icon={Truck} accent="#f59e0b" title="Shipment tracking on dispatch">
          Track a dispatch's shipment right from the row, with cross-stage polish so the journey from QC to Completed reads consistently.
        </RN13Item>

        <RN13Item icon={History} accent="#14b8a6" title="History Log, levelled up">
          The History Log now matches the stage pages' look, and its export carries proper parsed change columns with exact timestamps — clean enough to hand to accounting.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          …plus a fix so a bulk CSV update no longer clears blocks on matching items, and dozens of small refinements across the warehouse and the stages.
        </p>
      </div>

      {/* ── v2.0 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v2.0</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">The biggest RockLink yet.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={Warehouse} accent="#f97316" title="Stores — your raw material, finally inside RockLink">
          Every bar, forging and casting logged as a stock lot with its heat number and mill certificate attached. Reserve stock against a line item and it's issued automatically the moment kitting completes. Remnants tracked, nothing lost, and a full movement ledger behind it all.
        </RN13Item>

        <RN13Item icon={Users} accent="#3b82f6" title="New clients, without the wait">
          Anyone can request a new client right from where they work; admins approve with one click. No more typing jobs against the wrong client because the right one didn't exist yet.
        </RN13Item>

        <RN13Item icon={Sparkles} accent="#ef4444" title="RockLink, redesigned">
          The RL² identity, a rebuilt sign-in with the Centre of Excellence on display, and a quiet depth language across every page — lifted tiles, carved search wells, lit buttons. Same RockLink, calmer and faster to read.
        </RN13Item>

        <RN13Item icon={Moon} accent="#a855f7" title="Day shift, night shift, your shift">
          Choose Light, Dark, or Auto in the user menu — Auto follows your device, so RockLink dims when your evening does. Press <strong>N</strong> to cycle anytime.
        </RN13Item>

        <RN13Item icon={Gauge} accent="#0891b2" title="Smooth on every terminal">
          Reduce visual effects: one switch turns off the depth and glass for older shop-floor machines, keeping everything snappy.
        </RN13Item>

        <RN13Item icon={ShieldAlert} accent="#e11d48" title="Blocked items now name names">
          When you block a line, tag exactly who you're waiting on. They get pinged in their mention inbox, and the row shows it until it's resolved. No more &ldquo;I thought you had it.&rdquo;
        </RN13Item>

        <RN13Item icon={FileSpreadsheet} accent="#10b981" title="Accounting speaks part numbers">
          Enter a part's ERP code once and it fills in everywhere that part appears — past lines and future ones. A gentle nudge at Manufacturing makes sure nothing advances uncoded.
        </RN13Item>

        <RN13Item icon={Truck} accent="#f59e0b" title="Dispatch paperwork at a glance">
          Ticket and invoice numbers with their dates, right on the dispatch row — appearing exactly when dispatch work starts, quiet until then.
        </RN13Item>

        <RN13Item icon={Activity} accent="#14b8a6" title="The factory's pulse, one card">
          Live Activity now groups bursts of work, shows who did what in a uniform ledger, and every entry jumps straight to its item. Plus a monthly dispatch donut by client — the month's story in one look.
        </RN13Item>

        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed pl-10">
          …and more: multiple route cards per item, click-through @mentions, night-mode contrast polish, and dozens of small refinements across every stage.
        </p>
      </div>

      {/* ── v1.8.8 (demoted) ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.8.8</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Client groups, dashboard intelligence &amp; open notes.</span>
        </div>
      </div>
      <div className="space-y-3">

        <RN13Item icon={Layers} accent="#3b82f6" title="Client Groups — new Admin panel">
          Organise clients into named groups and subgroups directly from Admin Settings. Each group has its own colour, and clients can be assigned or reassigned across groups. Changes reflect everywhere in real time.
        </RN13Item>

        <RN13Item icon={Activity} accent="#f97316" title="Client Overview — new dashboard widget">
          Replaces the old client breakdown with a richer card: proportional colour bar across all groups, expandable group rows showing subgroups and individual clients, Active / Total toggle, column-aligned Lines and Value stats sorted by value.
        </RN13Item>

        <RN13Item icon={LayoutDashboard} accent="#f59e0b" title="Dashboard — side-by-side widget pairing">
          Client Overview and Top Blocking Reasons now sit side by side at equal height. Live Activity and Monthly Dispatches also share a row — with matched title styles and visual symmetry throughout.
        </RN13Item>

        <RN13Item icon={Users} accent="#e11d48" title="Client Group permissions — dynamic access">
          When limiting a user's client access, admins can now select entire client groups in addition to individual clients. Group membership is resolved at query time, so any new client added to a group is automatically visible to permitted users — no re-saving required.
        </RN13Item>

        <RN13Item icon={Calendar} accent="#10b981" title="Monthly Dispatches — bars now track ship date">
          Dispatched bars in the chart now bucket items by actual <strong>Shipped On</strong> date instead of due date. This means the chart bars and the header strip count always agree.
        </RN13Item>

        <RN13Item icon={StickyNote} accent="#a855f7" title="Notes open to all users">
          Any authenticated user can now write notes on any line item in any stage, regardless of their stage edit or write permissions. Notes remain readable and writable for read-only roles.
        </RN13Item>
      </div>

      {/* ── v1.8.5 separator ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.8.5</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Spend permissions, dispatch intelligence &amp; row enhancements.</span>
        </div>
      </div>

      {/* ── v1.8.5 ── */}
      <div className="space-y-3">

        <RN13Item icon={Eye} accent="#10b981" title="View Procurement Spend — new permission">
          Separated spend visibility from value visibility. Users can now see material / tooling / gauging spend amounts without seeing item USD values. Toggleable per-user in the Permissions modal; existing users auto-migrated to <em>spend visible</em>.
        </RN13Item>

        <RN13Item icon={Filter} accent="#f59e0b" title="Filter tags below stage chevrons">
          When a client, priority, or due-date filter is active, compact colour-matched tags bulge out beneath each stage chevron showing the filtered count. Active stage tags adopt the stage colour; inactive stages show grey. Tags animate in and brighten on hover.
        </RN13Item>

        <RN13Item icon={Truck} accent="#FFCB00" title="Dispatch — Shipped On column with filter">
          Dispatch and Completed stages now show a dedicated <strong>Shipped On</strong> column with the same filter/sort popover as Due Date. Shipped dates display in green <code>DD-Mon'YY</code> format. Completed stage defaults to newest-first order.
        </RN13Item>

        <RN13Item icon={Calendar} accent="#e11d48" title="Early / Late badges on Due Date">
          For items that have been shipped, the Due Date cell now shows an <strong>Xd early</strong> (green) or <strong>Xd late</strong> (red) badge calculated against the actual Shipped On date, replacing the generic overdue indicator.
        </RN13Item>

        <RN13Item icon={Layers} accent="#f97316" title="Duplicate part marker">
          When a part number appears on multiple line items, an amber pill in the Part# cell shows the count of other occurrences. Clicking opens a popover with Stage · PO · LN · Qty columns, each row navigating directly to that stage pre-filtered.
        </RN13Item>

        <RN13Item icon={Activity} accent="#3b82f6" title="Live Activity — richer timestamps">
          Activity feed now loads 120 entries. Timestamps older than 3 hours show a clock time; entries from previous calendar days show <code>DD-Mon HH:MM</code> format. Stage Movements panel follows the same format.
        </RN13Item>

        <RN13Item icon={Factory} accent="#a855f7" title="Manufacturing — Kit Done filter">
          The "Queued" status filter in Manufacturing is renamed <strong>Kit Done, Queued for Mfg</strong> and now only counts items where kitting is actually complete — giving a true ready-for-machining view.
        </RN13Item>

        <RN13Item icon={ClipboardCheck} accent="#14b8a6" title="QC — Route card badge">
          Items in QC Check now display a green highlighted <strong>RC: XXXXX</strong> badge between the client badge and the item name when a router card number is assigned.
        </RN13Item>
      </div>

      {/* ── v1.8.0 separator ── */}
      <div className="pt-1 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-baseline gap-2 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.8.0</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Multi-vertical, smarter targets, live sync &amp; more.</span>
        </div>
      </div>

      {/* ── v1.8.0 items ── */}
      <div className="space-y-3">
        <RN13Item icon={SlidersHorizontal} accent="#e11d48" title="Client Access Restriction">
          Admins can now lock any user to a specific set of clients. Toggle + searchable selector in User Permissions. Enforced server-side — restricted users see only their allowed clients across all stages, dashboard stats, and history log.
        </RN13Item>

        <RN13Item icon={Target} accent="#f97316" title="Race to a Number — target mode">
          Monthly targets now have two modes. <strong>PO Line Wise</strong> works as before. <strong>Race to a Number</strong> sets a plain dispatch count (e.g. 100 items) — any item shipped that month counts, regardless of PO or client.
        </RN13Item>

        <RN13Item icon={Filter} accent="#a855f7" title="Procurement Sub-teams — redesigned">
          Tighter 3-column row layout (pill · bar · stats). Each pill filters to pending items; Ordered and Complete chips are also clickable filters. Slow animated stripe on the ordered segment. Right tile shows procurement velocity — flips to a Review CTA when items are fully procured.
        </RN13Item>

        <RN13Item icon={Truck} accent="#10b981" title="Dispatched This Month — drill-down">
          Click the dispatched-count strip on the dashboard to open a sortable table of everything shipped this month with value summary and top-client callout.
        </RN13Item>

        <RN13Item icon={Package} accent="#0891b2" title="Line Split">
          Split any item into two with proportional qty and spend. Parent absorbs the remainder; child carries a <code>split_from_id</code> lineage link. Duplicate-line collision guard built in.
        </RN13Item>

        <RN13Item icon={Activity} accent="#f97316" title="Stage Velocity Tiles — Dispatch &amp; QC">
          Dispatch and QC stage headers now show a compact <strong>Today / This week / This month</strong> counter tile. Dispatch counts items with a Shipped On date in the period; QC counts lines that cleared QC and entered Dispatch.
        </RN13Item>

        <RN13Item icon={RefreshCw} accent="#22c55e" title="Live Auto-Sync">
          Data now refreshes automatically every 45 seconds while the tab is visible and immediately on tab focus-regain (with a 5 s grace window to ignore quick switches). The <strong>Online</strong> pill in the header shows Live · Syncing… · Xm ago status in real time.
        </RN13Item>

        <RN13Item icon={StickyNote} accent="#f59e0b" title="Stage-aware Notes badge">
          The notes badge on each row is now <strong>red</strong> only when at least one note was written in the item's current stage. Notes carried over from earlier stages show a <strong>grey</strong> badge so the counter stays visible without creating false urgency.
        </RN13Item>

        <RN13Item icon={Calendar} accent="#14b8a6" title="Monthly Dispatches chart — accuracy fixes">
          &ldquo;Total this month&rdquo; and all KPI cards now count by actual <strong>Shipped On</strong> date. Race-to-a-Number mode and PO Line Wise mode each show the correct subset; the duplicate counter is hidden in Race mode.
        </RN13Item>
      </div>

      {/* ── Previous releases collapsed ── */}
      <div className="pt-4 border-t border-[hsl(var(--border))]/60">
        <button
          type="button"
          onClick={() => setShowPrev(s => !s)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-[hsl(var(--muted))]/40 transition-colors"
          data-testid="release-notes-prev-toggle"
          aria-expanded={showPrev}
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Previous releases</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]">v1.7 &amp; earlier</span>
          </div>
          <span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">{showPrev ? "Hide" : "Show"}</span>
        </button>

        {showPrev && (
          <div className="mt-3 space-y-5">
            {/* v1.7 recap */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.7</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Dispatch tightened, Manufacturing expanded.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li><strong>Admin Announcements</strong> — broadcast a message on the login screen or as an in-app amber bar.</li>
                <li><strong>Dispatch Delivered gate</strong> — advance button only activates once status is Shipped; all legacy items auto-migrated.</li>
                <li><strong>Shipped On modal</strong> — captures the actual ship date when marking an item Shipped.</li>
                <li><strong>Manufacturing — Third Party status</strong> — vendor + ETA prompt; shown as a compact note on the row.</li>
                <li><strong>Live activity · Global feed</strong> — real-time scrollable feed of every change with field-level diffs.</li>
                <li><strong>Monthly Target in chart</strong> — target counter embedded in the Dispatches chart; non-admin target setters supported.</li>
                <li><strong>My Stage Movements</strong> (was Stage Inbox) — compact overdue rows, coloured stage chips, dark-mode-aware expanded surface.</li>
              </ul>
            </div>

            {/* v1.6 recap */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.6</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Less running around, more parts moving.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li>Stage Inbox bell — stage-specific notifications for arrivals, returns, blocks, expedites and overdue items.</li>
                <li>Notes on every line with <strong>@mentions</strong> — tag teammates, get pinged when tagged.</li>
                <li>RC Pending + Kitting Pending clickable tiles in the Manufacturing stats strip.</li>
                <li>QD Pending tile on QC — items passed but waiting on dossier sign-off.</li>
                <li>Shipped On column in Completed — auto-stamped; admin backfill for legacy items.</li>
                <li>Field-by-field history diffs (old value muted, new value emerald).</li>
                <li>FY26-27 Monthly Dispatches chart with target line and Q1–Q4 bands.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.5</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Visual clarity, smarter filters, one-click exports.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li>Monthly Targets on the Dashboard with live dispatched / total counter and a progress bar.</li>
                <li>Manufacturing gate — Router Card + Kitting checkpoints unlock the status dropdown.</li>
                <li>Excel-style auto-applying column filters on Due / Status across Mfg / QC / Dispatch.</li>
                <li>Status Glyphs: each status now has its own readable shape, no more flat dots.</li>
                <li>STATS strip — every count is a clickable filter; rework badge with a subtle lustre.</li>
                <li>Export this view button on every stage (filtered + sorted CSV).</li>
              </ul>
            </div>

            {/* v1.4 recap (compact) */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.4</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Procurement, QC and accountability — sharpened.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li>Procurement statuses simplified to Pending → Ordered → Partially Received → Complete with one-shot ETA + spend dialog.</li>
                <li>Spend pill opens a full add / soft-delete audit history; per-subteam spend permissions per user.</li>
                <li>Ready-for-Manufacturing alert + lightbox surfaces fully-procured items still sitting in Procurement.</li>
                <li>Per-row Details &amp; History modal with full per-stage timeline (who, when, what).</li>
                <li>Quality Dossier checkpoint gates Dispatch advance until QD is ticked off.</li>
                <li>QC Rework auto-routes via the Send-Back modal; status flips reset cleanly.</li>
                <li>Blocked items demand a "Specify next action" plan + accountable owner.</li>
                <li>Dashboard sparklines + clearer click affordance on Blocked / Overdue cards.</li>
              </ul>
            </div>

            {/* v1.3 recap (compact) */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.3</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Small fixes, real time saved every day.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li>Tap Blocked / Overdue on the dashboard for a ranked worst-first list.</li>
                <li>QC can attach up to 20 SharePoint / OneDrive drawing links when returning work.</li>
                <li>40% shorter Procurement rows in Compact mode; Spent now sits beneath the order value.</li>
                <li><strong>Delivered</strong> dispatch status + a proper Completed stage with Completed-On column.</li>
                <li>Presence sorts by heartbeat (not login); silver-sheen advance animation.</li>
                <li>Universal <strong>PO</strong> / <strong>LINE</strong> prefixes everywhere.</li>
                <li>Reports + daily digest call out blocked / rework with age + owner.</li>
              </ul>
            </div>

            {/* v1.2 recap (compact) */}
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">v1.2</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Bulk Add, reason codes, attention reports.</span>
              </div>
              <ul className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed list-disc pl-5 space-y-0.5">
                <li><strong>Bulk Add</strong> ingests vendor PO spreadsheets with line-by-line diffs.</li>
                <li>Per-stage reason codes with inline guidelines; "not known" forces free-text fallback.</li>
                <li>Top-Blocking-Reasons chart + one-click <strong>Generate Attention Report</strong>.</li>
                <li>Stock-ticker arrows on the daily digest stages.</li>
                <li>History Log: Part No / Revision / Line No / PO No on every entry; bulk imports collapse to one summary.</li>
                <li>Online pill is clickable — see who's live now and when offline teammates last logged in.</li>
                <li>Compact / Comfortable density toggle; quick rework / blocked filters.</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-[hsl(var(--border))]/60">
        <p className="text-sm leading-relaxed text-[hsl(var(--foreground))]">
          If anything feels off or you think of a feature that'd save your team a minute a day, drop me a line — the smaller the idea the better.
        </p>
        <p className="text-sm leading-relaxed text-[hsl(var(--foreground))] mt-4">
          Happy RockLinking!
        </p>
        <p className="text-sm leading-relaxed text-[hsl(var(--foreground))] mt-3">
          Best Regards,<br />
          <span className="font-semibold">Tushar Dhawan</span><br />
          <span className="text-[hsl(var(--muted-foreground))]">Director</span><br />
          <a
            href="mailto:tdhawan@rockpecker.com"
            className="text-orange-500 hover:text-orange-400 transition-colors"
            data-testid="release-notes-email-link"
          >
            tdhawan@rockpecker.com
          </a>
        </p>
      </div>
    </div>
  );
}
